import { Controller } from "@hotwired/stimulus"
import SceneDocument, { newPart, generateClientId } from "../editor/document"
import { AddPartCommand, RemovePartCommand, UpdatePartCommand, UpdatePartsCommand, CompositeCommand,
         SetVariablesCommand } from "../editor/commands"
import { anchorOf, withAnchor, descendantIdsOf, wouldCreateCycle, alignDelta, distributePositions, alignToEdgeDelta } from "../editor/relations"
import { jointContact, jointPositions, jointCutouts, jointIssueMessage, joinSummary,
         jointIdFor, cutoutsWithoutJoint } from "../editor/joinery"
import { constraintFromCurrent, constraintsOf, withConstraints, solveFor, cascadeUpdates, followerUpdates } from "../editor/constraints"
import History from "../editor/history"
import SceneSync from "../editor/sync"
import { collabAction, updatedByLabel, presenceSummary } from "../editor/collab"
import { subscribeToProject } from "../channels/project_channel"
import { restorePendingIntoScene } from "../editor/offline_store"
import { snapAngle, nudgeDelta } from "../editor/snapping"
import { isHidden, withHidden, isolateUpdates, showAllUpdates } from "../editor/visibility"
import { collidingPairs, outsideRoomIds } from "../editor/collision"
import { runChecks, countsBySeverity, shelfAssessment } from "../editor/checks"
import { SHELF_LOAD_KG_PER_SQM } from "../editor/deflection"
import { rectShape, circleShape, polygonShape, lineShape, isConstruction, shapeIsValid, shapeToPartAttrs,
         nearFirstPoint, hitEdge, hitFace, edgeLength, withEdgeLength,
         hitCorner, cornerRadius, withCornerRadius, isEdgeLocked, withEdgeLockToggled,
         sketchReferencePoints, snapSketchPoint as computeSketchSnap,
         planeFromHit, viewForPlane, planeLabel, planeAxisField } from "../editor/sketch"
import { buildCabinet, cabinetAnchorPos, CABINET_DEFAULTS } from "../editor/cabinet"
import { buildDrawer, buildDoor, drawerAnchorPos, doorAnchorPos, DRAWER_DEFAULTS, DOOR_DEFAULTS } from "../editor/drawer"
import { mergeGeneratedGroup, droppedMessage } from "../editor/generated_groups"
import { collectGroup, partsToTemplate, templateToParts } from "../editor/templates"
import { cutoutsOf, withCutouts, clampCutout, isCircle, cutoutAxis, toEdgeDistance, fromEdgeDistance } from "../editor/cutouts"
import { CORNER_KEYS, cornersOf, withCorner } from "../editor/corners"
import { groovesOf, withGrooves, clampGroove, isFalz } from "../editor/machinings"
import { mitersOf, withMiter, withoutMiter } from "../editor/miters"
import { grainOf, withGrain, grainLabel, grainSymbol } from "../editor/grain"
import { mirroredMeta } from "../editor/mirror"
import { hardwareOf, withHardware, withoutHardware } from "../editor/hardware"
import { edgesOf, toggleEdge } from "../editor/edges"
import { pickMeasureElement, measureBetween, formatMeasureText } from "../editor/measure"
import { partWeight, tippingRisk, formatWeight, weightSummary, hiddenCount } from "../editor/weight"
import { duplicatedParts, batchMoveUpdates, batchMaterialUpdates } from "../editor/multiselect"
import { groupOf, withGroup, withoutGroup, groupMemberIds, nextGroupName } from "../editor/groups"
import { patternCopies, defaultPatternSpacing } from "../editor/pattern"
import { holeRowCutouts, HOLE_ROW_DEFAULTS, holeRowIdOf, holeRowSize, withoutHoleRow } from "../editor/hole_row"
import { filterParts, isFilterActive } from "../editor/parts_filter"
import { clampSectionOffset, defaultSectionOffset } from "../editor/section"
import { assemblyOrder, effectivePartType } from "../editor/assembly"
import { withPartType } from "../editor/part_types"
import { clampedMeta } from "../editor/resize"
import { BINDABLE_FIELDS, variableNames, getVariable, setVariable, uniqueVariableName,
         renameVariable, removeVariable, boundName, withBinding, partsBoundTo, applyVariable,
         orphanBindings, orphanNames, clearOrphanBindings,
         normalizeValue, materialThicknessOf, MATERIAL_THICKNESS_NAME } from "../editor/variables"
import ThreeRenderer from "../editor/render/three_renderer"
import PointerInput from "../editor/input/pointer"
import COMMANDS, { searchCommands, commandForShortcut, shortcutLabel,
  isAllowedInMode, modeLabel } from "../editor/commands_registry"

// Wie lange eine Meldung den Speicherstatus verdrängt
const NOTICE_MS = 4000

const PRESETS = {
  plate: { name: "Platte", length_mm: 800, width_mm: 600, thickness_mm: 19 },
  beam: { name: "Balken", length_mm: 2000, width_mm: 60, thickness_mm: 40 }
}

// Augen-Icons für die Teile-Liste (24er-Raster und Strichstil wie
// EditorIconsHelper — die Zeilen entstehen hier im JS, nicht im ERB)
const EYE_ICONS = {
  open: '<path d="M2.5 12S6 5.8 12 5.8 21.5 12 21.5 12 18 18.2 12 18.2 2.5 12 2.5 12z"/><circle cx="12" cy="12" r="2.8"/>',
  closed: '<path d="M2.5 12S6 5.8 12 5.8c1.7 0 3.2.4 4.6 1.1M21.5 12S18 18.2 12 18.2c-1.7 0-3.2-.4-4.6-1.1"/><path d="M4.5 19.5l15-15"/>'
}

function eyeSvg(hidden) {
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
    stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5" aria-hidden="true">${hidden ? EYE_ICONS.closed : EYE_ICONS.open}</svg>`
}

// Zahlenwert eines Eingabefelds; leer/Unsinn ergibt NaN, damit die
// Domänen-Helfer ihre eigenen Vorgaben einsetzen können
function numberField(target) {
  return parseFloat(target.value)
}

// Konstruktions-Checkliste: Beschriftung und Farbgebung je Schweregrad
const SEVERITY_LABELS = { error: "Fehler", warning: "Warnung", hint: "Hinweis" }
const SEVERITY_STYLES = {
  error: "border-red-700 bg-red-950/50 hover:border-red-500",
  warning: "border-amber-700 bg-amber-950/40 hover:border-amber-500",
  hint: "border-stone-600 hover:border-stone-400"
}
const SEVERITY_TEXT = { error: "text-red-400", warning: "text-amber-400", hint: "text-stone-400" }

// Die rechten Panels. Sie liegen alle auf demselben Platz — am Handy auf dem
// Bottom-Sheet (inset-x-0 bottom-16), ab sm oben rechts (sm:top-3 sm:right-3).
// Zwei gleichzeitig gäbe es also nie zu sehen, nur übereinander. Darum sind sie
// gegenseitig exklusiv: Wer eines öffnet, schließt die anderen.
//
// Das Details-Panel gehört fachlich dazu. Es sitzt auf genau demselben Platz und
// ist der häufigste Begleiter der Teile-Liste (Teil in der Liste antippen →
// Details) — ließe man es außen vor, verdeckte ausgerechnet es weiterhin alles
// andere. Sein „Wunsch“-Zustand panelOpen läuft deshalb mit (siehe setSidePanel).
const SIDE_PANELS = {
  parts:     { panel: "partsPanel",     button: "partsButton" },
  templates: { panel: "templatesPanel", button: "templatesButton" },
  variables: { panel: "variablesPanel", button: "variablesButton" },
  checks:    { panel: "checksPanel",    button: "checksButton" },
  details:   { panel: "panel",          button: "panelButton" }
}

// Beschriftung der Bohrseite je Bohrachse (vgl. cutouts.js)
const CUTOUT_SIDE_LABELS = {
  y: { top: "von oben", bottom: "von unten" },
  x: { top: "rechte Stirn", bottom: "linke Stirn" },
  z: { top: "hintere Kante", bottom: "vordere Kante" }
}

// Holzoptik im Arbeits-Modus: reine Ansichtssache, deshalb lokal gemerkt
// (wie die aufgeklappten Panel-Sektionen) statt im Projekt gespeichert.
const WOOD_LOOK_KEY = "plattenwerk-wood-look"

// Einzige Brücke zwischen Rails-DOM und der 3D-Insel.
export default class extends Controller {
  static targets = [
    "canvas", "overlay", "status", "panel", "conflict",
    "name", "length", "width", "thickness", "posX", "posY", "posZ", "rotX", "rotY", "rotZ", "material", "grain", "partType",
    "cutoutList", "edgeButton", "undoButton", "redoButton", "contextBar",
    "pickHint", "relationBar", "relationRefName", "alignSection", "alignRefName", "attachInfo", "anchorName",
    "joinTypeButton", "constraintList",
    "distributeSection", "distributeCount", "cornerType", "cornerSize", "grooveList", "miterButton",
    "section", "partsPanel", "partsList", "partsCount", "showAllButton", "cutoutBadge", "grooveBadge",
    "partsSearch", "partsOnlyVisibleButton", "partsFilterCount",
    "weightSummary", "tippingHint", "comButton", "partWeight", "deflection",
    "checksPanel", "checksList", "checksBadge",
    "partsButton", "templatesButton", "variablesButton", "checksButton",
    "hardwareList", "hardwareBadge",
    "subBar", "subBarLabel", "subBarActions", "panelButton",
    "sketchBar", "sketchThickness", "sketchTool", "sketchExtrude", "sketchSelect", "sketchNameInput",
    "sketchEdgeBox", "sketchEdgeLabel", "sketchEdgeInput", "sketchEdgeLock",
    "actionBar", "sketchPlane", "sketchPlaneButton", "sketchPlaneLabel", "emptyState",
    "viewsMenu", "viewsButton", "woodLookButton",
    "collisionBadge", "roomBadge",
    "roomDialog", "roomWidth", "roomHeight", "roomDepth", "roomEnabled",
    "cabinetDialog", "cabinetWidth", "cabinetHeight", "cabinetDepth", "cabinetThickness", "cabinetShelves", "cabinetBack",
    "cabinetTitle", "cabinetSubmit", "cabinetEditButton",
    "groupLabel", "groupNameInput", "ungroupButton",
    "patternDialog", "patternHint", "patternCount", "patternSpacing", "patternAxisButton",
    "holeRowDialog", "holeRowHint", "holeRowCount", "holeRowPitch", "holeRowDiameter", "holeRowDepth",
    "holeRowStart", "holeRowEdge", "holeRowDirectionButton",
    "drawerDialog", "drawerTab", "doorTab", "drawerPane", "doorPane",
    "drawerWidth", "drawerDepth", "drawerHeight", "drawerThickness",
    "doorWidth", "doorHeight", "doorThickness", "doorHinge",
    "drawerTabs", "drawerTitle", "drawerSubmit", "doorSubmit",
    "drawerEditButton", "drawerEditIcon", "doorEditIcon",
    "measureButton", "measureHint",
    "templateDialog", "templateDialogHint", "templateName", "templatesPanel", "templatesList",
    "variablesPanel", "variablesList", "bindLength", "bindWidth", "bindThickness",
    "multiBar", "multiCount", "multiMaterial", "multiDx", "multiDy", "multiDz",
    "sectionButton", "sectionMenu", "sectionAxis", "sectionFlip", "sectionSlider",
    "photoButton", "photoSaveButton",
    "assemblyBar", "assemblySlider", "assemblyStepLabel", "assemblyPartName", "assemblyPrev", "assemblyNext",
    "paletteDialog", "paletteInput", "paletteList",
    "presence", "collabHint", "collabHintText"
  ]
  static values = {
    sceneUrl: String, scene: Object, templatesUrl: String,
    // Live-Kollaboration: Projekt fürs Kanal-Abo, eigene ID zum Aussortieren
    // der eigenen Präsenz- und Änderungs-Nachrichten
    projectId: Number, userId: Number
  }

  connect() {
    this.doc = new SceneDocument()
    this.history = new History(this.doc)
    this.selectedId = null
    this.gridMm = 10
    this.panelOpen = false // Details-Panel öffnet nur auf Wunsch
    this.joinType = "duebel" // Verbinder-Typ für den Verbindungs-Assistenten
    // Filter der Teile-Liste: reiner UI-Zustand, nicht persistiert
    this.partsFilter = { query: "", onlyVisible: false }

    this.renderer = new ThreeRenderer(this.canvasTarget, this.overlayTarget)
    this.renderer.onSelectedScreenPos = (pos) => this.positionContextBar(pos)
    this.pointer = new PointerInput(this.canvasTarget, {
      renderer: this.renderer,
      document: this.doc,
      grid: () => this.gridMm,
      onSelect: (clientId) => this.select(clientId),
      onMoveEnd: (clientId, pos) => this.movePart(clientId, pos),
      onMovePreview: (clientId, pos) => this.previewMove(clientId, pos),
      onDoubleTap: (clientId) => this.doubleTapped(clientId),
      onHandleTap: (handle) => this.setSubSelection(handle),
      onEdgeResize: (clientId, attrs) => this.resizePart(clientId, attrs),
      onRotateEnd: (clientId, attrs) => this.rotatePart(clientId, attrs)
    })
    this.sync = new SceneSync(this.doc, {
      url: this.sceneUrlValue,
      csrfToken: document.querySelector("meta[name=csrf-token]")?.content,
      onStatus: (state, detail) => this.renderStatus(state, detail),
      beforeFlush: () => this.doc.setCamera(this.renderer.cameraState())
    })

    this.unsubscribe = this.doc.onChange((event) => this.documentChanged(event))
    const { scene, restored } = restorePendingIntoScene(this.sceneValue, this.sceneUrlValue)
    this.doc.loadFrom(scene)
    this.populateMaterialSelect()
    this.sync.start(scene.version)
    if (restored) this.sync.markDirty()
    this.renderer.restoreCamera(this.doc.camera)

    this.keydownBound = (e) => this.keydown(e)
    addEventListener("keydown", this.keydownBound)
    this.paletteOpen = false
    this.macKeys = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || "")
    this.applyShortcutTooltips()
    this.restoreSections()
    this.restoreWoodLook()
    this.refreshButtons()
    this.updateJoinTypeButtons()
    this.updatePartsCount()
    this.startCollab()

    window.__editor = this // Test-Hook für Playwright (statt Pixel-Assertions)
  }

  // --- Live-Kollaboration ----------------------------------------------------
  // Zweiter Bearbeiter am selben Projekt: Präsenz anzeigen und fremde
  // Änderungen nachziehen. Siehe app/channels/project_channel.rb für den
  // Kontrakt und editor/collab.js für die Entscheidungslogik.

  // WICHTIG: Das Abo ist rein additiv. Kommt keine WebSocket-Verbindung
  // zustande (Cable abgeschaltet, Proxy ohne Upgrade, Adapter tot), bleibt
  // `this.collab` schlicht null und der Editor verhält sich exakt wie vorher —
  // Autosave und lock_version tragen die Korrektheit ohnehin allein. Darum
  // setzt hier nichts aus, was den Editor betrifft, und Fehler werden still
  // geschluckt statt gemeldet.
  startCollab() {
    if (!this.hasProjectIdValue || !this.projectIdValue) return
    this.collab = subscribeToProject({
      projectId: this.projectIdValue,
      onMessage: (message) => this.collabMessage(message)
    })
  }

  // Einzige Eintrittsstelle für Kanal-Nachrichten — auch der Playwright-Test
  // speist hier ein, statt einen zweiten Browser zu öffnen.
  collabMessage(message) {
    if (message?.type === "presence") return this.renderPresence(message.users)

    const action = collabAction({
      message,
      selfUserId: this.hasUserIdValue ? this.userIdValue : null,
      dirty: this.sync.dirty,
      inFlight: this.sync.inFlight
    })
    if (action === "reload") this.pullScene(message)
    else if (action === "notify") this.showCollabHint(message)
  }

  // Szene still vom Server nachladen. Kein location.reload(): Kamera, Modus und
  // offene Panels sollen stehen bleiben — nur der Inhalt wird getauscht.
  async pullScene(message) {
    if (this.collabPulling) return // paralleler Nachzieh-Lauf bringt nichts Neues
    this.collabPulling = true
    try {
      const response = await fetch(this.sceneUrlValue, { headers: { "Accept": "application/json" } })
      if (!response.ok) return
      const scene = await response.json()
      if (!this.renderer) return // disconnect während des Ladens
      this.applyPulledScene(scene)
      this.notify(updatedByLabel(message))
    } catch {
      // Kein Netz oder Serverfehler: Der nächste Broadcast (oder spätestens
      // der 409 beim eigenen Speichern) holt das nach.
    } finally {
      this.collabPulling = false
    }
  }

  applyPulledScene(scene) {
    this.hideCollabHint()
    // loadFrom emittiert "loaded" — documentChanged baut daraufhin die 3D-Szene,
    // Skizzen-Visuals und das Raummaß neu auf und markiert NICHT dirty. Genau
    // deshalb löst das Nachladen keinen PATCH und damit auch keine Schleife aus.
    this.doc.loadFrom(scene)
    this.populateMaterialSelect()
    this.sync.adoptVersion(scene.version)
    // Kamera bewusst NICHT wiederherstellen: doc.camera trägt jetzt den Stand
    // des anderen Bearbeiters, und der Nutzer schaut hier vielleicht gerade
    // ganz woanders hin. Der nächste eigene Flush schreibt die eigene Kamera
    // ohnehin wieder mit (beforeFlush).
    //
    // Der Verlauf dagegen wird verworfen: Seine Commands beziehen sich auf
    // Teile, die es nach dem Tausch womöglich nicht mehr gibt — ein Undo
    // darüber hinweg ergäbe einen Stand, den nie jemand konstruiert hat.
    this.history = new History(this.doc)
    // Auswahl überlebt, solange es das Teil noch gibt
    if (this.selectedId && !this.doc.getPart(this.selectedId)) {
      this.selectedId = null
      this.panelTarget.classList.add("hidden")
    }
    if (this.multiSelect) {
      for (const id of [ ...this.multiSelect.ids ]) {
        if (!this.doc.getPart(id)) this.multiSelect.ids.delete(id)
      }
      this.renderer.setMultiHighlight([ ...this.multiSelect.ids ])
    }
    this.renderer.setSelected(this.selectedId)
    this.renderer.setGroupHighlight(this.linkedPartIdsFor(this.selectedId))
    if (this.selectedId) this.fillPanel()
    // Skizzen-Modus: Indizes der alten Shapes zeigen ins Leere
    if (this.sketchMode) {
      this.clearSketchSelection()
      this.sketchMode.draft = null
      this.sketchMode.poly = []
      this.updateSketchUi()
    }
    this.refreshButtons()
    this.updatePartsCount()
    if (!this.partsPanelTarget.classList.contains("hidden")) this.renderPartsList()
  }

  showCollabHint(message) {
    if (!this.hasCollabHintTarget) return
    this.collabHintTextTarget.textContent = `${updatedByLabel(message)} — deine Änderungen sind noch ungespeichert.`
    this.collabHintTarget.classList.remove("hidden")
  }

  hideCollabHint() {
    if (this.hasCollabHintTarget) this.collabHintTarget.classList.add("hidden")
  }

  // Initialen-Punkte der anderen Bearbeiter; allein im Projekt bleibt der
  // ganze Bereich unsichtbar, damit der Header nicht unruhig wird.
  renderPresence(users) {
    if (!this.hasPresenceTarget) return
    const summary = presenceSummary(users, this.hasUserIdValue ? this.userIdValue : null)
    const host = this.presenceTarget
    host.replaceChildren()
    host.classList.toggle("hidden", summary.count === 0)
    host.classList.toggle("flex", summary.count > 0)
    if (summary.count === 0) return

    host.title = summary.title
    const label = document.createElement("span")
    label.className = "sr-only"
    label.textContent = summary.label
    host.appendChild(label)
    for (const dot of summary.dots) {
      const chip = document.createElement("span")
      chip.className = "inline-flex items-center justify-center w-6 h-6 rounded-full " +
        "bg-stone-600 text-[9px] font-medium text-stone-100"
      chip.textContent = dot.initials
      chip.title = dot.name
      host.appendChild(chip)
    }
    if (summary.overflow > 0) {
      const more = document.createElement("span")
      more.className = "text-[10px] text-stone-400 pl-0.5"
      more.textContent = `+${summary.overflow}`
      host.appendChild(more)
    }
  }

  // --- Standard-Ansichten ----------------------------------------------------

  toggleViewsMenu() {
    const open = this.viewsMenuTarget.classList.toggle("hidden")
    this.viewsButtonTarget.classList.toggle("bg-amber-600", !open)
    this.viewsButtonTarget.classList.toggle("text-white", !open)
  }

  closeViewsMenu() {
    if (this.hasViewsMenuTarget && !this.viewsMenuTarget.classList.contains("hidden")) this.toggleViewsMenu()
  }

  setView(event) {
    this.renderer.setStandardView(event.params.view)
    this.closeViewsMenu()
  }

  // --- Holzoptik (Ansichten-Menü) --------------------------------------------
  // Zeigt die Holz-Texturen des Foto-Modus auch beim Arbeiten — ohne dessen
  // Hintergrund-/Licht-Umbau. Der Zustand überlebt den Reload (Muster wie die
  // Panel-Sektionen). Das Menü bleibt beim Umschalten offen, damit der
  // Unterschied sofort sichtbar ist und ein zweiter Tipp ihn zurücknimmt.
  toggleWoodLook() {
    this.setWoodLook(!this.woodLook)
    try { localStorage.setItem(WOOD_LOOK_KEY, this.woodLook ? "1" : "0") } catch { /* egal */ }
  }

  restoreWoodLook() {
    let stored = null
    try { stored = localStorage.getItem(WOOD_LOOK_KEY) } catch { /* egal */ }
    this.setWoodLook(stored === "1")
  }

  setWoodLook(enabled) {
    this.woodLook = Boolean(enabled)
    this.renderer.setWoodLook(this.woodLook)
    if (!this.hasWoodLookButtonTarget) return
    this.woodLookButtonTarget.classList.toggle("bg-amber-600", this.woodLook)
    this.woodLookButtonTarget.classList.toggle("text-white", this.woodLook)
    this.woodLookButtonTarget.setAttribute("aria-pressed", String(this.woodLook))
  }

  // 3D-Export als glTF (.glb) aus dem Ansichten-Menü: sichtbare Teile mit
  // Materialfarben als Blob-Download, Dateiname = Projektname aus dem Header
  async exportGltf() {
    this.closeViewsMenu()
    try {
      const blob = await this.renderer.exportGlb()
      const name = this.element.querySelector("input[name='project[name]']")?.value?.trim()
        || document.title?.trim() || "Unbenanntes Projekt"
      const link = document.createElement("a")
      link.href = URL.createObjectURL(blob)
      link.download = `${name}.glb`
      link.click()
      setTimeout(() => URL.revokeObjectURL(link.href), 5000)
    } catch {
      this.notify("3D-Export fehlgeschlagen")
    }
  }

  // --- Raummaß (Nischen-/Raumbegrenzung, persistiert in doc.room) ------------

  ROOM_DEFAULTS = { width_mm: 3000, height_mm: 2500, depth_mm: 600 }

  openRoomDialog() {
    this.closeViewsMenu()
    const room = this.doc.room
    this.roomWidthTarget.value = room?.width_mm ?? this.ROOM_DEFAULTS.width_mm
    this.roomHeightTarget.value = room?.height_mm ?? this.ROOM_DEFAULTS.height_mm
    this.roomDepthTarget.value = room?.depth_mm ?? this.ROOM_DEFAULTS.depth_mm
    this.roomEnabledTarget.checked = room?.enabled === true
    this.roomDialogTarget.classList.remove("hidden")
  }

  closeRoomDialog() {
    this.roomDialogTarget.classList.add("hidden")
  }

  // Übernehmen: doc.room setzen (abgeschaltet = {}), Sync anstoßen,
  // Visuals und Außerhalb-Warnung sofort nachziehen
  applyRoom() {
    const value = (target, fallback) => {
      const n = parseInt(target.value, 10)
      return Number.isFinite(n) && n > 0 ? n : fallback
    }
    this.doc.room = this.roomEnabledTarget.checked
      ? {
          enabled: true,
          width_mm: value(this.roomWidthTarget, this.ROOM_DEFAULTS.width_mm),
          height_mm: value(this.roomHeightTarget, this.ROOM_DEFAULTS.height_mm),
          depth_mm: value(this.roomDepthTarget, this.ROOM_DEFAULTS.depth_mm)
        }
      : {}
    this.sync.markDirty()
    this.renderer.setRoom(this.doc.room)
    this.scheduleWarningCheck()
    this.closeRoomDialog()
  }

  // --- Kollisions- und Raum-Warnungen ----------------------------------------

  // Nach Dokument-Änderungen leicht entprellt neu rechnen — documentChanged
  // feuert ohnehin nur bei echten Commits, nicht pro Drag-Frame
  scheduleWarningCheck() {
    clearTimeout(this.warningTimer)
    this.warningTimer = setTimeout(() => this.refreshWarnings(), 150)
  }

  refreshWarnings() {
    if (!this.renderer) return // Timer kann nach disconnect noch feuern
    const parts = this.doc.allParts()
    this.collisionPairs = collidingPairs(parts)
    this.outsideRoom = outsideRoomIds(parts, this.doc.room)
    this.renderer.setWarnings({
      collisionIds: [ ...new Set(this.collisionPairs.flat()) ],
      roomIds: this.outsideRoom
    })
    const collisions = this.collisionPairs.length
    this.collisionBadgeTarget.textContent = `⚠ ${collisions} ${collisions === 1 ? "Kollision" : "Kollisionen"}`
    this.collisionBadgeTarget.classList.toggle("hidden", collisions === 0)
    const outside = this.outsideRoom.length
    this.roomBadgeTarget.textContent = `⚠ ${outside} außerhalb Raum`
    this.roomBadgeTarget.classList.toggle("hidden", outside === 0)
    this.refreshChecks(parts)
  }

  // --- Konstruktions-Checkliste ----------------------------------------------

  // Läuft im selben entprellten Durchgang wie die Kollisionsprüfung mit —
  // die Regeln teilen sich ohnehin deren O(n²)-Paarsuche.
  refreshChecks(parts) {
    this.checkFindings = runChecks(parts, { materials: [ ...this.doc.materials.values() ] })
    const counts = countsBySeverity(this.checkFindings)
    const badge = this.checksBadgeTarget
    badge.textContent = String(this.checkFindings.length)
    badge.title = `${counts.error} Fehler, ${counts.warning} Warnungen, ${counts.hint} Hinweise`
    badge.classList.toggle("bg-red-700", counts.error > 0)
    badge.classList.toggle("bg-amber-600", counts.error === 0 && counts.warning > 0)
    badge.classList.toggle("bg-stone-600", counts.error === 0 && counts.warning === 0)
    if (!this.checksPanelTarget.classList.contains("hidden")) this.renderChecks()
  }

  toggleChecksPanel() {
    this.setSidePanel("checks", !this.sidePanelOpen("checks"))
  }

  renderChecks() {
    const list = this.checksListTarget
    list.replaceChildren()
    const findings = this.checkFindings ?? []
    if (findings.length === 0) {
      const empty = document.createElement("p")
      empty.className = "text-xs text-stone-400 py-2"
      empty.textContent = this.doc.allParts().length === 0
        ? "Noch keine Teile." : "Keine Funde — die Konstruktion ist plausibel."
      list.appendChild(empty)
      return
    }
    for (const entry of findings) {
      const row = document.createElement("button")
      row.className = "w-full text-left rounded-lg border px-3 py-2 " + SEVERITY_STYLES[entry.severity]
      row.title = "Betroffenes Teil anzeigen"
      const head = document.createElement("div")
      head.className = "text-[10px] uppercase tracking-wide " + SEVERITY_TEXT[entry.severity]
      head.textContent = SEVERITY_LABELS[entry.severity]
      const text = document.createElement("div")
      text.className = "text-xs text-stone-100"
      text.textContent = entry.message
      row.append(head, text)
      row.addEventListener("click", () => this.focusWarnPart(entry.clientIds[0]))
      list.appendChild(row)
    }
  }

  // Tipp aufs Badge: erstes betroffenes Teil selektieren und anfahren

  focusCollision() {
    this.focusWarnPart(this.collisionPairs?.[0]?.[0])
  }

  focusOutsideRoom() {
    this.focusWarnPart(this.outsideRoom?.[0])
  }

  focusWarnPart(clientId) {
    if (!clientId || !this.doc.getPart(clientId)) return
    this.select(clientId)
    this.renderer.focusOnPart(clientId)
  }

  // --- Skizzen-Modus (zeichnen auf der Bodenebene, dann extrudieren) ---------

  startSketch() {
    if (this.sketchMode) return
    this.exitPhoto()
    this.cancelPick()
    this.stopMeasure()
    this.stopSection()
    this.setSubSelection(null)
    this.select(null)
    // Shapes leben im Dokument (persistiert, überlebt Verlassen + Reload);
    // mit bestehender Skizze startet der Wähl-Modus, sonst das Zeichnen
    if (this.doc.sketches.length === 0) this.doc.addSketch()
    this.sketchMode = {
      tool: this.doc.sketch.length > 0 ? "select" : "rect",
      draft: null, poly: [], strokeStart: null,
      selectedFaces: [], selectedEdge: null, selectedCorner: null,
      snap: null, pickingPlane: false
    }
    // Start senkrecht auf der Skizzier-Ebene, aber frei drehbar:
    // Rechteck/Kreis/Linie belegen den Ein-Finger-Drag (capturesPointer),
    // Wählen/Polygon nur Taps — Drag = Orbit
    this.renderer.setStandardView(viewForPlane(this.doc.sketchPlane))
    this.pointer.sketch = {
      capturesPointer: () => [ "rect", "circle", "line" ].includes(this.sketchMode?.tool),
      plane: () => this.doc.sketchPlane,
      pickingSurface: () => !!this.sketchMode?.pickingPlane,
      surfaceTap: (hit) => this.sketchPlanePicked(hit),
      down: (p) => this.sketchDown(p),
      move: (p) => this.sketchMove(p),
      up: (p) => this.sketchUp(p),
      tap: (p) => this.sketchTap(p),
      abortStroke: () => { if (this.sketchMode) { this.sketchMode.draft = null; this.renderSketch() } }
    }
    // Skizze bekommt die volle Leiste — normale Steuerelemente treten ab
    this.actionBarTarget.classList.add("hidden")
    this.closeSidePanels()
    this.sketchBarTarget.classList.remove("hidden")
    this.renderSketch()
    this.updateSketchUi()
    this.updatePartsCount()
  }

  setSketchTool(event) {
    if (!this.sketchMode) return
    this.sketchMode.tool = event.params.tool
    if (this.sketchMode.tool !== "poly") this.sketchMode.poly = []
    if (this.sketchMode.tool !== "select") this.clearSketchSelection()
    this.sketchMode.draft = null
    this.renderSketch()
    this.updateSketchUi()
  }

  // Fangender Punkt: Referenzpunkte (Ecken, Mitten, Zentren, Linien-Enden)
  // vor Achsen-Fang vor Raster; Fang-Feedback landet im Snap-State
  snapSketchPoint(p) {
    const refs = sketchReferencePoints(this.doc.sketch)
    const result = computeSketchSnap(p, {
      refs, gridMm: this.gridMm, tolerance: this.renderer.worldToleranceFor(14)
    })
    this.sketchMode.snap = result.snap
      ? { ...result.snap, point: { x: result.x, z: result.z } }
      : null
    return result
  }

  // Nur Rechteck/Kreis/Linie kommen hier an (capturesPointer);
  // Wählen/Polygon laufen über sketchTap
  sketchDown(p) {
    const s = this.sketchMode
    const point = this.snapSketchPoint(p)
    if (s.tool === "rect") s.draft = rectShape(point, point)
    else if (s.tool === "line") s.draft = lineShape(point, point)
    else if (s.tool === "circle") { s.strokeStart = point; s.draft = circleShape(point, point) }
    this.renderSketch()
  }

  sketchMove(p) {
    const s = this.sketchMode
    if (!s.draft) return
    const point = this.snapSketchPoint(p)
    if (s.tool === "rect" || s.tool === "line") { s.draft.bx = point.x; s.draft.bz = point.z }
    else if (s.tool === "circle") s.draft = circleShape(s.strokeStart, point)
    this.renderSketch()
  }

  sketchUp() {
    const s = this.sketchMode
    if (s.draft && shapeIsValid(s.draft)) this.pushSketchShape(s.draft)
    s.draft = null
    s.strokeStart = null
    s.snap = null
    this.renderSketch()
    this.updateSketchUi()
  }

  // Tap-Werkzeuge: Wählen trifft Kanten/Flächen, Polygon setzt Punkte —
  // ein Drag dreht stattdessen die Kamera (Orbit bleibt aktiv)
  sketchTap(p) {
    const s = this.sketchMode
    if (!s) return
    if (s.tool === "select") return this.sketchSelectAt(p)
    if (s.tool !== "poly") return
    const point = this.snapSketchPoint(p)
    if (s.poly.length >= 3 && nearFirstPoint(s.poly, point)) {
      const shape = polygonShape(s.poly)
      if (shapeIsValid(shape)) this.pushSketchShape(shape)
      s.poly = []
    } else {
      s.poly.push([ point.x, point.z ])
    }
    this.renderSketch()
    this.updateSketchUi()
  }

  pushSketchShape(shape) {
    this.doc.sketch.push(shape)
    this.sync.markDirty()
  }

  // Wähl-Modus: Kante antippen → Maß bearbeiten; Fläche antippen → für die
  // Extrusion an-/abwählen; ins Leere tippen → Auswahl leeren
  sketchSelectAt(p) {
    const s = this.sketchMode
    const shapes = this.doc.sketch
    // Ecken haben Vorrang vor Kanten: Tap auf einen poly-Stützpunkt → Eckradius
    const corner = hitCorner(shapes, p, this.renderer.worldToleranceFor(16))
    if (corner) {
      s.selectedCorner = { shapeIndex: corner.shapeIndex, cornerIndex: corner.cornerIndex }
      s.selectedEdge = null
      s.selectedFaces = []
      this.renderSketch()
      this.updateSketchUi()
      return
    }
    s.selectedCorner = null
    const edge = hitEdge(shapes, p, this.renderer.worldToleranceFor(16))
    if (edge) {
      s.selectedEdge = { shapeIndex: edge.shapeIndex, edgeIndex: edge.edgeIndex }
      s.selectedFaces = []
    } else {
      s.selectedEdge = null
      const face = hitFace(shapes, p)
      if (face !== null) {
        s.selectedFaces = s.selectedFaces.includes(face)
          ? s.selectedFaces.filter((i) => i !== face)
          : [ ...s.selectedFaces, face ]
      } else {
        s.selectedFaces = []
      }
    }
    this.renderSketch()
    this.updateSketchUi()
  }

  applySketchEdge() {
    const s = this.sketchMode
    if (!s?.selectedEdge && !s?.selectedCorner) return
    const value = parseFloat(this.sketchEdgeInputTarget.value)
    if (s.selectedCorner) {
      // Ecke gewählt → Eingabe ist der Eckradius (0 = scharfe Ecke)
      if (!Number.isFinite(value) || value < 0) return
      const { shapeIndex, cornerIndex } = s.selectedCorner
      this.doc.sketch[shapeIndex] = withCornerRadius(this.doc.sketch[shapeIndex], cornerIndex, value)
    } else {
      if (!Number.isFinite(value) || value <= 0) return
      const { shapeIndex, edgeIndex } = s.selectedEdge
      this.doc.sketch[shapeIndex] = withEdgeLength(this.doc.sketch[shapeIndex], edgeIndex, value)
    }
    this.sync.markDirty()
    this.renderSketch()
    this.updateSketchUi()
  }

  // Schloss-Toggle: Maß der gewählten Kante sperren bzw. wieder freigeben
  toggleSketchEdgeLock() {
    const s = this.sketchMode
    if (!s?.selectedEdge) return
    const { shapeIndex, edgeIndex } = s.selectedEdge
    this.doc.sketch[shapeIndex] = withEdgeLockToggled(this.doc.sketch[shapeIndex], edgeIndex)
    this.sync.markDirty()
    this.renderSketch()
    this.updateSketchUi()
  }

  sketchUndo() {
    const s = this.sketchMode
    if (!s) return
    if (s.draft) s.draft = null
    else if (s.poly.length > 0) s.poly.pop()
    else if (this.doc.sketch.length > 0) {
      this.doc.sketch.pop()
      this.sync.markDirty()
      this.clearSketchSelection()
    }
    this.renderSketch()
    this.updateSketchUi()
  }

  clearSketchSelection() {
    const s = this.sketchMode
    if (!s) return
    s.selectedFaces = []
    s.selectedEdge = null
    s.selectedCorner = null
  }

  renderSketch() {
    const s = this.sketchMode
    if (!s) {
      // außerhalb des Modus: alle Skizzen dezent stehen lassen (je auf ihrer
      // effektiven Ebene — gekoppelte folgen ihrem Teil)
      const layers = this.doc.sketches
        .filter((sk) => sk.shapes.length > 0)
        .map((sk) => ({ plane: this.doc.resolvedSketchPlane(sk), shapes: sk.shapes }))
      this.renderer.setSketchVisuals(layers.length > 0
        ? { shapes: layers[0].shapes, plane: layers[0].plane, idle: true, idleLayers: layers.slice(1) }
        : null)
      return
    }
    // Nicht-aktive Skizzen bleiben als dezente Orientierung sichtbar
    const idleLayers = this.doc.sketches
      .filter((sk) => sk.id !== this.doc.activeSketchId && sk.shapes.length > 0)
      .map((sk) => ({ plane: this.doc.resolvedSketchPlane(sk), shapes: sk.shapes }))
    this.renderer.setSketchVisuals({
      shapes: this.doc.sketch, draft: s.draft, poly: s.poly,
      selectedFaces: s.selectedFaces, selectedEdge: s.selectedEdge, selectedCorner: s.selectedCorner,
      snap: s.snap, plane: this.doc.sketchPlane, idleLayers
    })
  }

  updateSketchUi() {
    const s = this.sketchMode
    if (!s) return
    this.sketchSelectTarget.replaceChildren(
      ...this.doc.sketches.map((sk) => new Option(sk.name, sk.id))
    )
    this.sketchSelectTarget.value = this.doc.activeSketchId
    this.sketchToolTargets.forEach((el) => {
      const active = el.dataset.editorToolParam === s.tool
      el.classList.toggle("bg-amber-600", active)
      el.classList.toggle("text-white", active)
      el.classList.toggle("bg-stone-700", !active)
    })

    const edge = s.selectedEdge
    const corner = s.selectedCorner
    this.sketchEdgeBoxTarget.classList.toggle("hidden", !edge && !corner)
    this.sketchEdgeLockTarget.classList.toggle("hidden", !edge) // Sperren gibt es nur für Kantenmaße
    this.sketchEdgeInputTarget.min = corner ? 0 : 1 // Radius 0 = scharfe Ecke
    this.sketchEdgeInputTarget.disabled = false
    if (corner) {
      this.sketchEdgeLabelTarget.textContent = "Radius"
      this.sketchEdgeInputTarget.value = Math.round(cornerRadius(this.doc.sketch[corner.shapeIndex], corner.cornerIndex))
    } else if (edge) {
      const shape = this.doc.sketch[edge.shapeIndex]
      this.sketchEdgeLabelTarget.textContent = shape.kind === "circle" ? "Ø" : shape.kind === "line" ? "Länge" : "Maß"
      this.sketchEdgeInputTarget.value = Math.round(edgeLength(shape, edge.edgeIndex))
      // Gesperrtes Maß: Eingabe zu, Schloss geschlossen
      const locked = isEdgeLocked(shape, edge.edgeIndex)
      this.sketchEdgeInputTarget.disabled = locked
      this.sketchEdgeLockTarget.textContent = locked ? "🔒" : "🔓"
      this.sketchEdgeLockTarget.classList.toggle("text-amber-400", locked)
    }

    const plane = this.doc.sketchPlane
    if (document.activeElement !== this.sketchPlaneTarget) {
      this.sketchPlaneTarget.value = plane.offset
    }
    // Gekoppelte Ebene: Teilname mit anzeigen (⌖ = folgt diesem Teil)
    const refPart = plane.ref ? this.doc.getPart(plane.ref.client_id) : null
    this.sketchPlaneLabelTarget.textContent = refPart
      ? `${planeLabel(plane)} ⌖ ${refPart.name}`
      : planeLabel(plane)
    this.sketchPlaneButtonTarget.classList.toggle("bg-amber-600", s.pickingPlane)
    this.sketchPlaneButtonTarget.classList.toggle("text-white", s.pickingPlane)

    // Konstruktionslinien zählen nicht mit — sie werden nie extrudiert
    const validCount = this.doc.sketch.filter((shape) => shapeIsValid(shape) && !isConstruction(shape)).length
    const count = s.selectedFaces.length > 0 ? s.selectedFaces.length : validCount
    this.sketchExtrudeTarget.textContent = `Extrudieren (${count})`
    this.sketchExtrudeTarget.disabled = validCount === 0
  }

  // --- Referenzebene der Skizze (horizontal, Höhe in mm) ---------------------

  setSketchPlane() {
    const value = parseInt(this.sketchPlaneTarget.value, 10)
    // Manuelles Setzen löst eine Teil-Kopplung: der eingegebene Wert gilt absolut
    const plane = { ...this.doc.sketchPlane, offset: Number.isFinite(value) ? value : 0 }
    delete plane.ref
    this.doc.sketchPlane = plane
    this.sync.markDirty()
    this.renderSketch()
    this.updateSketchUi()
  }

  // Ebene von einer Teil-Oberfläche übernehmen: Button aktivieren,
  // dann Fläche antippen (surfaceTap über den Pointer)
  pickSketchPlane() {
    const s = this.sketchMode
    if (!s) return
    s.pickingPlane = !s.pickingPlane
    this.updateSketchUi()
  }

  // Angetippte Fläche wird die Skizzier-Ebene (auch senkrechte Flächen);
  // die Kamera schwenkt senkrecht auf die neue Ebene. Traf der Tipp ein Teil,
  // koppelt die Ebene daran: sie folgt später dessen Position auf der Normalachse
  sketchPlanePicked(hit) {
    const s = this.sketchMode
    if (!s) return
    const plane = planeFromHit(hit.point, hit.normal)
    const part = hit.clientId ? this.doc.getPart(hit.clientId) : null
    if (part) plane.ref = { client_id: part.client_id, axis_pos: part[planeAxisField(plane)] }
    this.doc.sketchPlane = plane
    s.pickingPlane = false
    this.sync.markDirty()
    this.renderer.setStandardView(viewForPlane(this.doc.sketchPlane))
    this.renderSketch()
    this.updateSketchUi()
  }

  // Gewähltes Element (Kante → ganzes Shape, oder gewählte Flächen) entfernen
  deleteSketchElement() {
    const s = this.sketchMode
    if (!s) return
    const indices = s.selectedEdge ? [ s.selectedEdge.shapeIndex ] : [ ...s.selectedFaces ]
    if (indices.length === 0) return
    for (const index of indices.sort((a, b) => b - a)) this.doc.sketch.splice(index, 1)
    this.clearSketchSelection()
    this.sync.markDirty()
    this.renderSketch()
    this.updateSketchUi()
  }

  finishSketch() {
    const s = this.sketchMode
    if (!s) return
    const thickness = parseFloat(this.sketchThicknessTarget.value) || 19
    const shapes = s.selectedFaces.length > 0
      ? s.selectedFaces.map((i) => this.doc.sketch[i]).filter(Boolean)
      : this.doc.sketch
    const commands = shapes
      .map((shape) => shapeToPartAttrs(shape, thickness, this.doc.sketchPlane))
      .filter(Boolean)
      .map((attrs) => new AddPartCommand(newPart(attrs)))
    if (commands.length > 0) {
      this.history.execute(commands.length === 1 ? commands[0] : new CompositeCommand(commands))
    }
    this.exitSketch()
    if (commands.length > 0) this.renderer.frameAll()
  }

  cancelSketch() {
    this.exitSketch()
  }

  // Aktive Skizze komplett entfernen; ohne verbleibende startet eine frische
  deleteSketch() {
    this.doc.removeSketch(this.doc.activeSketchId)
    if (this.doc.sketches.length === 0) this.doc.addSketch()
    this.sketchActivated()
  }

  // --- Mehrere Skizzen: anlegen und wechseln ---------------------------------

  addSketch() {
    if (!this.sketchMode) return
    this.doc.addSketch()
    this.sketchActivated()
  }

  switchSketch(event) {
    if (!this.sketchMode) return
    this.doc.setActiveSketch(event.target.value)
    this.sketchActivated()
  }

  // --- Skizze umbenennen: das Select wird kurzzeitig zum Textfeld ------------

  renameSketch() {
    if (!this.sketchMode || !this.doc.activeSketch) return
    this.sketchNameInputTarget.value = this.doc.activeSketch.name
    this.sketchSelectTarget.classList.add("hidden")
    this.sketchNameInputTarget.classList.remove("hidden")
    this.sketchNameInputTarget.focus()
    this.sketchNameInputTarget.select()
  }

  // Enter/Blur übernimmt (leer → alter Name bleibt); der Name persistiert
  // über toJSON automatisch mit ins sketch_state
  applySketchRename() {
    if (this.sketchNameInputTarget.classList.contains("hidden")) return // Blur nach Enter/Escape
    const name = this.sketchNameInputTarget.value.trim()
    this.closeSketchRename()
    if (!name || !this.doc.activeSketch || name === this.doc.activeSketch.name) return
    this.doc.activeSketch.name = name
    this.sync.markDirty()
    this.updateSketchUi()
  }

  cancelSketchRename() {
    this.closeSketchRename()
  }

  closeSketchRename() {
    this.sketchNameInputTarget.classList.add("hidden")
    this.sketchSelectTarget.classList.remove("hidden")
  }

  // Nach jedem Skizzenwechsel: Zeichenzustand zurücksetzen, Kamera senkrecht
  // auf die Ebene der nun aktiven Skizze
  sketchActivated() {
    const s = this.sketchMode
    if (!s) return
    s.tool = this.doc.sketch.length > 0 ? "select" : "rect"
    s.draft = null
    s.poly = []
    s.pickingPlane = false
    this.clearSketchSelection()
    this.sync.markDirty()
    this.renderer.setStandardView(viewForPlane(this.doc.sketchPlane))
    this.renderSketch()
    this.updateSketchUi()
  }

  // Verlassen behält die Skizze (dezent sichtbar); Wiedereinstieg über „Skizze“
  exitSketch() {
    if (!this.sketchMode) return
    this.sketchMode = null
    this.pointer.sketch = null
    this.sketchBarTarget.classList.add("hidden")
    this.actionBarTarget.classList.remove("hidden")
    this.renderSketch()
    this.updatePartsCount()
  }

  // --- Korpus-Generator -------------------------------------------------------

  openCabinetDialog() {
    this.editingCabinet = null
    this.cabinetTitleTarget.textContent = "Korpus erzeugen"
    this.cabinetSubmitTarget.textContent = "Erzeugen"
    this.prefillMaterialThickness(this.cabinetThicknessTarget)
    this.cabinetDialogTarget.classList.remove("hidden")
  }

  // Gibt es eine Variable „Materialstärke“, ist sie die Vorgabe im Generator —
  // sonst bleibt der Wert im Feld stehen. Der erzeugte Korpus wird dadurch
  // nicht automatisch gebunden (das entscheidet der Nutzer im Details-Panel).
  prefillMaterialThickness(target) {
    const mm = materialThicknessOf(this.doc.variables)
    if (mm !== null) target.value = mm
  }

  closeCabinetDialog() {
    this.editingCabinet = null
    this.cabinetDialogTarget.classList.add("hidden")
  }

  // Anker (Seite links, trägt meta.cabinet) zur Korpusgruppe des Teils
  cabinetAnchorFor(part) {
    const groupId = part?.meta?.cabinet_id
    if (!groupId) return null
    return this.doc.allParts().find((p) => p.meta?.cabinet_id === groupId && p.meta?.cabinet) ?? null
  }

  // Alle Teile derselben Generator-Gruppe (Korpus, Schublade oder Tür) — für
  // die Mithervorhebung im 3D-Raum. Eine Schublade im Korpus gehört beiden
  // Gruppen an, deshalb werden alle Gruppen-Schlüssel des Teils ausgewertet.
  generatedGroupPartIdsFor(clientId) {
    const part = this.doc.getPart(clientId)
    if (!part) return []
    const ids = new Set()
    for (const key of [ "cabinet_id", "drawer_id", "door_id" ]) {
      const groupId = part.meta?.[key]
      if (!groupId) continue
      for (const other of this.doc.allParts()) {
        if (other.meta?.[key] === groupId) ids.add(other.client_id)
      }
    }
    return [ ...ids ]
  }

  // Mithervorhebung: Generator-Gruppe UND Baugruppe des Teils — die Baugruppe
  // ist die größere Einheit, beide Mengen werden vereinigt (Mechanik geteilt)
  linkedPartIdsFor(clientId) {
    const part = clientId ? this.doc.getPart(clientId) : null
    if (!part) return []
    const ids = new Set(this.generatedGroupPartIdsFor(clientId))
    const group = groupOf(part)
    if (group) for (const id of groupMemberIds(this.doc.allParts(), group.id)) ids.add(id)
    return [ ...ids ]
  }

  // Dialog im Bearbeiten-Modus: mit gespeicherten Parametern vorbefüllt
  editCabinet() {
    const anchor = this.cabinetAnchorFor(this.doc.getPart(this.selectedId))
    if (!anchor) return
    const params = anchor.meta.cabinet
    this.cabinetWidthTarget.value = params.width
    this.cabinetHeightTarget.value = params.height
    this.cabinetDepthTarget.value = params.depth
    this.cabinetThicknessTarget.value = params.thickness
    this.cabinetShelvesTarget.value = params.shelves
    this.cabinetBackTarget.checked = params.back
    this.cabinetDialogTarget.classList.remove("hidden")
    this.cabinetTitleTarget.textContent = "Korpus bearbeiten"
    this.cabinetSubmitTarget.textContent = "Übernehmen"
    this.editingCabinet = anchor.meta.cabinet_id
  }

  createCabinet() {
    const value = (target, fallback) => {
      const n = parseInt(target.value, 10)
      return Number.isFinite(n) ? n : fallback
    }
    const editingId = this.editingCabinet
    const anchor = editingId
      ? this.doc.allParts().find((p) => p.meta?.cabinet_id === editingId && p.meta?.cabinet)
      : null
    const params = {
      width: value(this.cabinetWidthTarget, CABINET_DEFAULTS.width),
      height: value(this.cabinetHeightTarget, CABINET_DEFAULTS.height),
      depth: value(this.cabinetDepthTarget, CABINET_DEFAULTS.depth),
      thickness: value(this.cabinetThicknessTarget, CABINET_DEFAULTS.thickness),
      shelves: Math.max(0, Math.min(10, value(this.cabinetShelvesTarget, CABINET_DEFAULTS.shelves))),
      back: this.cabinetBackTarget.checked,
      materialId: anchor
        ? anchor.material_id
        : (this.doc.materials.size > 0 ? [ ...this.doc.materials.keys() ][0] : null),
      groupId: editingId ?? undefined
    }
    const parts = buildCabinet(params)
    if (!parts) {
      this.notify("Korpusmaße unplausibel")
      return
    }

    if (anchor) {
      // Versatz im Raum, Handarbeit und Heftungen erhalten — gemeinsame
      // Mechanik mit dem Schubladen-/Tür-Zweig
      const oldIds = this.doc.allParts()
        .filter((p) => p.meta?.cabinet_id === editingId)
        .map((p) => p.client_id)
      this.closeCabinetDialog()
      this.replaceGeneratedGroup(anchor, cabinetAnchorPos(anchor.meta.cabinet), oldIds, parts)
      return
    }

    this.history.execute(new CompositeCommand(parts.map((part) => new AddPartCommand(part))))
    this.closeCabinetDialog()
    this.renderer.frameAll()
  }

  // --- Schubladen-/Tür-Generator ----------------------------------------------

  openDrawerDialog() {
    this.editingDrawer = null
    this.setDrawerEditMode(null)
    this.prefillMaterialThickness(this.drawerThicknessTarget)
    this.prefillMaterialThickness(this.doorThicknessTarget)
    this.drawerDialogTarget.classList.remove("hidden")
  }

  closeDrawerDialog() {
    this.editingDrawer = null
    this.setDrawerEditMode(null)
    this.drawerDialogTarget.classList.add("hidden")
  }

  // Tab-Umschalter im Dialog: Schublade ↔ Tür/Front
  selectDrawerPane(event) {
    this.showDrawerPane(event.params.pane === "door")
  }

  showDrawerPane(door) {
    this.drawerPaneTarget.classList.toggle("hidden", door)
    this.doorPaneTarget.classList.toggle("hidden", !door)
    for (const [ tab, active ] of [ [ this.drawerTabTarget, !door ], [ this.doorTabTarget, door ] ]) {
      tab.classList.toggle("bg-amber-600", active)
      tab.classList.toggle("text-white", active)
      tab.classList.toggle("font-medium", active)
      tab.classList.toggle("text-stone-300", !active)
    }
  }

  // Erstes Material des Projekts als Vorgabe (wie beim Korpus)
  defaultMaterialId() {
    return this.doc.materials.size > 0 ? [ ...this.doc.materials.keys() ][0] : null
  }

  // Korpus, in den eine neue Schublade/Tür gehört: der Korpus des gerade
  // ausgewählten Teils. Bewusst über die Auswahl statt über die Lage — beim
  // Erzeugen steht die Gruppe noch im Ursprung, eine räumliche Prüfung ginge
  // ins Leere. Für Bestandsprojekte holt die Sortierung die Zuordnung
  // räumlich nach (cabinetOfFront in editor/assembly.js).
  selectedCabinetId() {
    return this.doc.getPart(this.selectedId)?.meta?.cabinet_id ?? null
  }

  // Anker der Schubladen-/Türgruppe eines Teils (Muster: cabinetAnchorFor).
  // Anker ist das Teil mit den Parametern — bei der Schublade die linke Zarge,
  // bei der Tür die Tür selbst. Jedes Gruppenmitglied findet darüber hinein.
  drawerAnchorFor(part) {
    for (const [ idKey, kind ] of [ [ "drawer_id", "drawer" ], [ "door_id", "door" ] ]) {
      const groupId = part?.meta?.[idKey]
      if (!groupId) continue
      const anchor = this.doc.allParts().find((p) => p.meta?.[idKey] === groupId && p.meta?.[kind])
      if (anchor) return { anchor, kind, groupId }
    }
    return null
  }

  // Bearbeiten-Modus des Dialogs: Tabs ausblenden statt sperren (eine Schublade
  // wird nicht zur Tür), passenden Bereich erzwingen, Titel + Buttontext setzen.
  setDrawerEditMode(kind) {
    const editing = Boolean(kind)
    const door = kind === "door"
    if (this.hasDrawerTabsTarget) this.drawerTabsTarget.classList.toggle("hidden", editing)
    if (this.hasDrawerTitleTarget) {
      this.drawerTitleTarget.classList.toggle("hidden", !editing)
      this.drawerTitleTarget.textContent = door ? "Tür bearbeiten" : "Schublade bearbeiten"
    }
    if (editing) this.showDrawerPane(door)
    const label = editing ? "Übernehmen" : "Erzeugen"
    if (this.hasDrawerSubmitTarget) this.drawerSubmitTarget.textContent = label
    if (this.hasDoorSubmitTarget) this.doorSubmitTarget.textContent = label
  }

  // Dialog im Bearbeiten-Modus: mit gespeicherten Parametern vorbefüllt
  editDrawer() {
    const group = this.drawerAnchorFor(this.doc.getPart(this.selectedId))
    if (!group) return
    const params = group.anchor.meta[group.kind]
    if (group.kind === "door") {
      this.doorWidthTarget.value = params.width
      this.doorHeightTarget.value = params.height
      this.doorThicknessTarget.value = params.thickness ?? DOOR_DEFAULTS.thickness
      this.doorHingeTarget.value = params.hinge === "right" ? "right" : "left"
    } else {
      this.drawerWidthTarget.value = params.width
      this.drawerDepthTarget.value = params.depth
      this.drawerHeightTarget.value = params.height
      this.drawerThicknessTarget.value = params.thickness
    }
    this.editingDrawer = { groupId: group.groupId, kind: group.kind }
    this.setDrawerEditMode(group.kind)
    this.drawerDialogTarget.classList.remove("hidden")
  }

  // Gruppe durch neu gebaute Teile ersetzen — ein Undo-Schritt; gemeinsam für
  // Korpus, Schublade und Tür.
  // Positions-Delta zwischen aktueller und generierter Ankerposition erhält den
  // Platz im Raum. Handarbeit an den alten Teilen (Ausschnitte, Umleimer, Nuten,
  // Namen, Material …) wandert über die Rollen-Zuordnung auf die neuen Teile
  // (generated_groups.js). Zusätzlich werden fremde Teile, die per
  // meta.attached_to an einem entfernten Gruppenteil hingen, auf den neuen
  // Anker umgebogen, damit sie nicht verwaisen.
  replaceGeneratedGroup(anchor, generated, oldIds, built) {
    const delta = {
      x: anchor.pos_x - generated.x,
      y: anchor.pos_y - generated.y,
      z: anchor.pos_z - generated.z
    }
    for (const part of built) {
      part.pos_x += delta.x
      part.pos_y += delta.y
      part.pos_z += delta.z
    }
    const oldParts = oldIds.map((id) => this.doc.getPart(id)).filter(Boolean)
    const { parts, dropped } = mergeGeneratedGroup(oldParts, built)
    const removed = new Set(oldIds)
    // War die Gruppe selbst irgendwo eingeheftet (z. B. Schublade im Korpus),
    // erbt der neue Anker diese Heftung — sonst fiele sie beim Bearbeiten weg.
    const host = anchorOf(anchor)
    if (host && !removed.has(host)) parts[0].meta = withAnchor(parts[0], host)
    const rewired = this.doc.allParts()
      .filter((p) => !removed.has(p.client_id) && removed.has(anchorOf(p)))
      .map((p) => new UpdatePartCommand(p.client_id, { meta: withAnchor(p, parts[0].client_id) }))
    this.history.execute(new CompositeCommand([
      ...rewired,
      ...oldIds.map((id) => new RemovePartCommand(id)),
      ...parts.map((part) => new AddPartCommand(part))
    ]))
    this.select(parts[0].client_id)
    const message = droppedMessage(dropped)
    if (message) this.notify(message)
  }

  createDrawer() {
    const value = (target, fallback) => {
      const n = parseInt(target.value, 10)
      return Number.isFinite(n) ? n : fallback
    }
    const editingId = this.editingDrawer?.kind === "drawer" ? this.editingDrawer.groupId : null
    const anchor = editingId
      ? this.doc.allParts().find((p) => p.meta?.drawer_id === editingId && p.meta?.drawer)
      : null
    const parts = buildDrawer({
      ...(anchor?.meta?.drawer ?? {}), // Luft/Boden-/Frontstärke aus dem Bestand halten
      width: value(this.drawerWidthTarget, DRAWER_DEFAULTS.width),
      depth: value(this.drawerDepthTarget, DRAWER_DEFAULTS.depth),
      height: value(this.drawerHeightTarget, DRAWER_DEFAULTS.height),
      thickness: value(this.drawerThicknessTarget, DRAWER_DEFAULTS.thickness),
      materialId: anchor ? anchor.material_id : this.defaultMaterialId(),
      groupId: editingId ?? undefined,
      cabinetId: editingId ? null : this.selectedCabinetId()
    })
    if (!parts) {
      this.notify("Schubladenmaße unplausibel")
      return
    }
    if (anchor) {
      const oldIds = this.doc.allParts()
        .filter((p) => p.meta?.drawer_id === editingId)
        .map((p) => p.client_id)
      this.closeDrawerDialog()
      this.replaceGeneratedGroup(anchor, drawerAnchorPos(anchor.meta.drawer), oldIds, parts)
      return
    }
    this.history.execute(new CompositeCommand(parts.map((part) => new AddPartCommand(part))))
    this.closeDrawerDialog()
    this.renderer.frameAll()
  }

  createDoor() {
    const value = (target, fallback) => {
      const n = parseInt(target.value, 10)
      return Number.isFinite(n) ? n : fallback
    }
    const editingId = this.editingDrawer?.kind === "door" ? this.editingDrawer.groupId : null
    const anchor = editingId
      ? this.doc.allParts().find((p) => p.meta?.door_id === editingId && p.meta?.door)
      : null
    const door = buildDoor({
      ...(anchor?.meta?.door ?? {}),
      width: value(this.doorWidthTarget, DOOR_DEFAULTS.width),
      height: value(this.doorHeightTarget, DOOR_DEFAULTS.height),
      thickness: value(this.doorThicknessTarget, anchor?.meta?.door?.thickness ?? DOOR_DEFAULTS.thickness),
      hinge: this.doorHingeTarget.value === "right" ? "right" : "left",
      materialId: anchor ? anchor.material_id : this.defaultMaterialId(),
      groupId: editingId ?? undefined,
      cabinetId: editingId ? null : this.selectedCabinetId()
    })
    if (!door) {
      this.notify("Türmaße unplausibel")
      return
    }
    if (anchor) {
      const oldIds = this.doc.allParts()
        .filter((p) => p.meta?.door_id === editingId)
        .map((p) => p.client_id)
      this.closeDrawerDialog()
      this.replaceGeneratedGroup(anchor, doorAnchorPos(anchor.meta.door), oldIds, [ door ])
      return
    }
    this.history.execute(new CompositeCommand([ new AddPartCommand(door) ]))
    this.closeDrawerDialog()
    this.renderer.frameAll()
  }

  // --- Vorlagen-Bibliothek ----------------------------------------------------

  templateHeaders() {
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content,
      "Accept": "application/json"
    }
  }

  openTemplateDialog() {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    this.templateGroup = collectGroup(this.doc.allParts(), this.selectedId)
    this.templateDialogHintTarget.textContent = this.templateGroup.length === 1
      ? "1 Teil wird gespeichert."
      : `${this.templateGroup.length} Teile (inkl. verbundener) werden gespeichert.`
    this.templateNameTarget.value = part.name
    this.templateDialogTarget.classList.remove("hidden")
    this.templateNameTarget.focus()
    this.templateNameTarget.select()
  }

  closeTemplateDialog() {
    this.templateGroup = null
    this.templateDialogTarget.classList.add("hidden")
  }

  async saveTemplate() {
    const group = this.templateGroup
    const name = this.templateNameTarget.value.trim()
    if (!group || group.length === 0 || name === "") return
    const data = partsToTemplate(group, this.doc.materials)
    this.closeTemplateDialog()
    try {
      const response = await fetch(this.templatesUrlValue, {
        method: "POST", headers: this.templateHeaders(), body: JSON.stringify({ name, data })
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const count = data.parts.length
      this.notify(`Vorlage „${name}“ gespeichert (${count} ${count === 1 ? "Teil" : "Teile"})`)
    } catch {
      this.notify("Vorlage speichern fehlgeschlagen")
    }
  }

  toggleTemplatesPanel() {
    this.setSidePanel("templates", !this.sidePanelOpen("templates"))
  }

  async loadTemplates() {
    const message = (text) => {
      const p = document.createElement("p")
      p.className = "text-xs text-stone-400 py-2"
      p.textContent = text
      this.templatesListTarget.replaceChildren(p)
    }
    message("Lade Vorlagen…")
    try {
      const response = await fetch(this.templatesUrlValue, { headers: { "Accept": "application/json" } })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const templates = await response.json()
      if (templates.length === 0) return message("Noch keine Vorlagen — Teil wählen und „Als Vorlage…“ tippen.")
      this.renderTemplatesList(templates)
    } catch {
      message("Vorlagen laden fehlgeschlagen")
    }
  }

  renderTemplatesList(templates) {
    const list = this.templatesListTarget
    list.replaceChildren()
    for (const template of templates) {
      const row = document.createElement("div")
      row.className = "rounded-lg border border-stone-600 px-3 py-2"
      const title = document.createElement("div")
      title.className = "text-sm text-stone-100 truncate"
      title.textContent = template.name
      const meta = document.createElement("div")
      meta.className = "flex items-center justify-between mt-0.5"
      const count = document.createElement("span")
      count.className = "text-[10px] text-stone-400"
      count.textContent = `${template.parts_count} ${template.parts_count === 1 ? "Teil" : "Teile"}`
      const actions = document.createElement("span")
      const insert = document.createElement("button")
      insert.className = "text-xs text-amber-400 hover:text-amber-300 min-h-8 px-2"
      insert.textContent = "Einfügen"
      insert.addEventListener("click", () => this.insertTemplate(template))
      const remove = document.createElement("button")
      remove.className = "text-xs text-stone-400 hover:text-red-400 min-h-8 px-2"
      remove.textContent = "Löschen"
      remove.addEventListener("click", () => this.deleteTemplate(template))
      actions.append(insert, remove)
      meta.append(count, actions)
      row.append(title, meta)
      list.appendChild(row)
    }
  }

  // --- Parametrische Variablen -----------------------------------------------

  toggleVariablesPanel() {
    this.setSidePanel("variables", !this.sidePanelOpen("variables"))
  }

  // Variablenstand ändern und die gebundenen Teile in EINEM Undo-Schritt
  // nachziehen: Variablen-Tausch + UpdatePartsCommand als CompositeCommand.
  // partUpdates kommen aus applyVariable/renameVariable/removeVariable.
  commitVariables(variables, partUpdates = []) {
    const parts = this.doc.allParts()
    // Maßänderungen verschieben die Welt-AABB: eigene Sollposition aus den
    // Constraints neu lösen (genau wie resizePart) …
    const updates = partUpdates.map(({ clientId, attrs }) => {
      const part = this.doc.getPart(clientId)
      if (!part) return { clientId, attrs }
      const ownPos = solveFor({ ...part, ...attrs }, parts)
      return { clientId, attrs: ownPos ? { ...attrs, ...ownPos } : attrs }
    })
    // … und die Constraint-Follower gehören in denselben Command
    const followers = updates.length > 0 ? cascadeUpdates(parts, updates) : []

    const commands = [ new SetVariablesCommand(variables) ]
    if (updates.length + followers.length > 0) {
      commands.push(new UpdatePartsCommand([ ...updates, ...followers ]))
    }
    this.history.execute(new CompositeCommand(commands))
    this.renderVariablesList()
  }

  addVariable() {
    // Erste Variable heißt „Materialstärke“ — der mit Abstand häufigste Fall
    // und zugleich der Name, den die Generatoren als Vorgabe lesen
    const variables = this.doc.variables
    const name = variableNames(variables).length === 0
      ? MATERIAL_THICKNESS_NAME
      : uniqueVariableName(variables, "Variable")
    this.commitVariables(setVariable(variables, name, 19))
  }

  setVariableValue(name, raw) {
    const mm = normalizeValue(raw)
    if (mm === null || mm === getVariable(this.doc.variables, name)) return this.renderVariablesList()
    this.commitVariables(setVariable(this.doc.variables, name, mm),
                         applyVariable(this.doc.allParts(), name, mm))
  }

  renameVariableTo(oldName, raw) {
    const { variables, updates } = renameVariable(this.doc.variables, this.doc.allParts(), oldName, raw)
    if (Object.hasOwn(variables, oldName)) {
      // Name leer oder schon belegt — Feld zurücksetzen und Bescheid geben
      this.notify("Variablenname leer oder bereits vergeben")
      return this.renderVariablesList()
    }
    this.commitVariables(variables, updates)
  }

  deleteVariable(name) {
    const { variables, updates } = removeVariable(this.doc.variables, this.doc.allParts(), name)
    this.commitVariables(variables, updates)
  }

  // Tote Bindungen in EINEM Undo-Schritt lösen. Die Maße bleiben stehen: der
  // Wert der verschwundenen Variablen steckt ja bereits im Teil und ist genau
  // das, was gebaut werden soll — nur der Verweis fällt weg.
  removeOrphanBindings() {
    const updates = clearOrphanBindings(this.doc.allParts(), this.doc.variables)
    if (updates.length > 0) {
      this.history.execute(new UpdatePartsCommand(updates))
      this.notify(updates.length === 1)
        ? "Verwaiste Bindung entfernt"
        : `${updates.length} verwaiste Bindungen entfernt`
    }
    this.renderVariablesList()
  }

  // Hinweiskasten über der Liste, solange Bindungen auf gelöschte Variablen
  // zeigen — ohne ihn müsste man jedes Teil einzeln suchen und das Select im
  // Details-Panel von Hand auf „frei“ stellen. null = alles sauber.
  buildOrphanHint() {
    const parts = this.doc.allParts()
    const entries = orphanBindings(parts, this.doc.variables)
    if (entries.length === 0) return null

    const box = document.createElement("div")
    box.className = "rounded-lg border border-amber-500/60 bg-amber-500/10 px-2 py-2 mb-2"
    const text = document.createElement("p")
    text.className = "text-[11px] text-amber-300"
    text.textContent = entries.length === 1
      ? "1 Teil verweist auf eine gelöschte Variable"
      : `${entries.length} Teile verweisen auf gelöschte Variablen`
    const missing = orphanNames(parts, this.doc.variables).map((name) => `„${name}“`).join(", ")
    text.title = `Fehlt: ${missing}`
    const button = document.createElement("button")
    button.className = "mt-1 w-full min-h-8 rounded-lg border border-amber-500/60 text-xs text-amber-300 hover:text-amber-200"
    button.textContent = "Bindungen entfernen"
    button.title = "Löst die toten Bindungen — die Maße der Teile bleiben unverändert"
    button.addEventListener("click", () => this.removeOrphanBindings())
    box.append(text, button)
    return box
  }

  renderVariablesList() {
    if (!this.hasVariablesListTarget) return
    const list = this.variablesListTarget
    list.replaceChildren()
    const orphanHint = this.buildOrphanHint()
    if (orphanHint) list.appendChild(orphanHint)
    const names = variableNames(this.doc.variables)
    if (names.length === 0) {
      const hint = document.createElement("p")
      hint.className = "text-xs text-stone-400 py-2"
      hint.textContent = "Noch keine Variablen — „+ Variable“ anlegen und im Details-Panel an ein Maß binden."
      list.appendChild(hint)
    }
    for (const name of names) list.appendChild(this.buildVariableRow(name))
  }

  buildVariableRow(name) {
    const row = document.createElement("div")
    row.className = "rounded-lg border border-stone-600 px-2 py-2"

    const head = document.createElement("div")
    head.className = "flex items-center gap-1"
    const nameInput = document.createElement("input")
    nameInput.type = "text"
    nameInput.value = name
    nameInput.setAttribute("aria-label", "Variablenname")
    nameInput.className = "flex-1 min-w-0 rounded bg-stone-900 border border-stone-600 px-2 py-1 text-xs text-stone-100"
    nameInput.addEventListener("change", () => this.renameVariableTo(name, nameInput.value))
    const remove = document.createElement("button")
    remove.className = "text-xs text-stone-400 hover:text-red-400 min-h-8 px-2 shrink-0"
    remove.textContent = "Löschen"
    remove.title = `Variable „${name}“ löschen`
    remove.addEventListener("click", () => this.deleteVariable(name))
    head.append(nameInput, remove)

    const foot = document.createElement("div")
    foot.className = "flex items-center gap-2 mt-1"
    const value = document.createElement("input")
    value.type = "number"
    value.inputMode = "numeric"
    value.min = "1"
    value.value = this.doc.variables[name]
    value.setAttribute("aria-label", `Wert von ${name} in mm`)
    value.className = "w-20 rounded bg-stone-900 border border-stone-600 px-2 py-1 text-xs text-stone-100"
    value.addEventListener("change", () => this.setVariableValue(name, value.value))
    const unit = document.createElement("span")
    unit.className = "text-[10px] text-stone-500"
    unit.textContent = "mm"
    const count = partsBoundTo(this.doc.allParts(), name).length
    const badge = document.createElement("span")
    badge.className = "ml-auto text-[10px] text-stone-400"
    badge.textContent = `${count} ${count === 1 ? "Teil" : "Teile"}`
    foot.append(value, unit, badge)

    row.append(head, foot)
    return row
  }

  // Bindungs-Selects im Details-Panel: „frei“ plus alle Variablennamen
  fillBindSelects(part) {
    if (!part || !this.hasBindThicknessTarget) return
    const targets = {
      length_mm: this.bindLengthTarget,
      width_mm: this.bindWidthTarget,
      thickness_mm: this.bindThicknessTarget
    }
    const names = variableNames(this.doc.variables)
    for (const field of BINDABLE_FIELDS) {
      const select = targets[field]
      select.replaceChildren()
      const free = document.createElement("option")
      free.value = ""
      free.textContent = "frei"
      select.appendChild(free)
      for (const name of names) {
        const option = document.createElement("option")
        option.value = name
        option.textContent = name
        select.appendChild(option)
      }
      const bound = boundName(part, field)
      // An eine gelöschte Variable gebunden: Eintrag trotzdem zeigen, damit
      // das Select den Zustand des Teils nicht stillschweigend verfälscht
      if (bound && !names.includes(bound)) {
        const orphan = document.createElement("option")
        orphan.value = bound
        orphan.textContent = `${bound} (fehlt)`
        select.appendChild(orphan)
      }
      select.value = bound ?? ""
      select.classList.toggle("text-amber-400", Boolean(bound))
      select.classList.toggle("text-stone-300", !bound)
    }
  }

  // Maß an Variable binden/lösen. Binden übernimmt sofort den Variablenwert —
  // sonst behauptete das Panel eine Bindung, die nichts bewirkt.
  applyBinding(event) {
    const part = this.selectedId ? this.doc.getPart(this.selectedId) : null
    if (!part) return
    const field = event.target.dataset.field
    const name = event.target.value
    const meta = withBinding(part, field, name)
    const attrs = { meta }

    const value = name ? getVariable(this.doc.variables, name) : null
    if (value !== null && part[field] !== value) {
      attrs[field] = value
      const clamped = clampedMeta({ ...part, meta }, { ...part, meta, [field]: value })
      if (clamped) attrs.meta = clamped
    }

    const ownPos = solveFor({ ...part, ...attrs }, this.doc.allParts())
    this.executeWithConstraints(this.selectedId, ownPos ? { ...attrs, ...ownPos } : attrs)
  }

  insertTemplate(template) {
    const materialsByName = new Map([ ...this.doc.materials.values() ].map((m) => [ m.name, m ]))
    const center = this.sceneCenter()
    const parts = templateToParts(template.data, materialsByName, { x: center.x + 200, z: center.z })
    if (parts.length === 0) return
    this.history.execute(parts.length === 1
      ? new AddPartCommand(parts[0])
      : new CompositeCommand(parts.map((part) => new AddPartCommand(part))))
    this.setSidePanel("templates", false)
    this.select(parts[0].client_id)
    this.renderer.frameAll()
  }

  async deleteTemplate(template) {
    try {
      const response = await fetch(`${this.templatesUrlValue}/${template.id}`, {
        method: "DELETE", headers: this.templateHeaders()
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      this.loadTemplates()
    } catch {
      this.notify("Vorlage löschen fehlgeschlagen")
    }
  }

  // Zentrum der bestehenden Szene (Positions-Spannen) — Einfüge-Anker
  sceneCenter() {
    const parts = this.doc.allParts()
    if (parts.length === 0) return { x: 0, z: 0 }
    const mid = (values) => Math.round((Math.min(...values) + Math.max(...values)) / 2)
    return { x: mid(parts.map((p) => p.pos_x)), z: mid(parts.map((p) => p.pos_z)) }
  }

  // --- Foto-Modus (Präsentationsansicht + Bild-Export) ------------------------

  togglePhoto() {
    if (this.photoMode) return this.exitPhoto()
    if (this.sketchMode) return
    this.stopMeasure()
    this.cancelPick()
    this.stopSection() // Präsentation ohne Schnitt: sonst bliebe der Ebenen-Indikator im Bild
    this.setSubSelection(null)
    this.select(null)
    this.closeSidePanels() // Präsentation ohne Panels — und die Kürzel sind hier ohnehin gesperrt
    this.photoMode = true
    this.renderer.setPhotoMode(true)
    this.photoButtonTarget.classList.add("bg-amber-600", "text-white")
    this.photoButtonTarget.classList.remove("bg-stone-700")
    this.photoSaveButtonTarget.classList.remove("hidden")
  }

  exitPhoto() {
    if (!this.photoMode) return
    this.photoMode = false
    this.renderer.setPhotoMode(false)
    this.photoButtonTarget.classList.remove("bg-amber-600", "text-white")
    this.photoButtonTarget.classList.add("bg-stone-700")
    this.photoSaveButtonTarget.classList.add("hidden")
  }

  savePhoto() {
    if (!this.photoMode) return
    const link = document.createElement("a")
    link.href = this.renderer.capturePhoto()
    const name = this.element.querySelector("input[name='project[name]']")?.value?.trim()
    link.download = `${name || "projekt"}.png`
    link.click()
  }

  // --- Montage-Modus (Schritte + Explosionsansicht, rein visuell) ------------

  startAssembly() {
    if (this.assemblyMode || this.sketchMode) return
    const order = assemblyOrder(this.doc.allParts())
    if (order.length === 0) return
    this.cancelPick()
    this.stopMeasure()
    this.setSubSelection(null)
    this.select(null) // ohne Selektion gibt es auch keine Auswahl-Handles
    // Einstieg: letzter Schritt (alle Teile sichtbar), Explosion 0
    this.assemblyMode = { order, step: order.length }
    this.actionBarTarget.classList.add("hidden")
    this.closeSidePanels()
    this.assemblyBarTarget.classList.remove("hidden")
    this.assemblySliderTarget.value = 0
    this.renderer.setExplode(0)
    this.applyAssemblyStep()
  }

  setAssemblyExplode(event) {
    if (!this.assemblyMode) return
    const value = parseInt(event.target.value, 10)
    this.renderer.setExplode((Number.isFinite(value) ? value : 0) / 100)
  }

  assemblyPrevStep() { this.stepAssembly(-1) }
  assemblyNextStep() { this.stepAssembly(1) }

  stepAssembly(delta) {
    const a = this.assemblyMode
    if (!a) return
    a.step = Math.min(Math.max(a.step + delta, 1), a.order.length)
    this.applyAssemblyStep()
  }

  // Im Schritt n sind nur die Teile 1..n der Montagereihenfolge sichtbar;
  // das zuletzt hinzugekommene Teil steht namentlich in der Leiste.
  applyAssemblyStep() {
    const a = this.assemblyMode
    if (!a) return
    const visible = a.order.slice(0, a.step)
    this.renderer.setVisibleParts(visible.map((part) => part.client_id))
    this.assemblyStepLabelTarget.textContent = `Schritt ${a.step}/${a.order.length}`
    this.assemblyPartNameTarget.textContent = visible[visible.length - 1]?.name ?? ""
    this.assemblyPrevTarget.disabled = a.step <= 1
    this.assemblyNextTarget.disabled = a.step >= a.order.length
  }

  finishAssembly() {
    if (!this.assemblyMode) return
    this.assemblyMode = null
    this.renderer.setExplode(0)
    this.renderer.setVisibleParts(null)
    this.assemblyBarTarget.classList.add("hidden")
    this.actionBarTarget.classList.remove("hidden")
  }

  // Dokument kann sich im Modus ändern (z. B. Sync aus anderem Fenster):
  // Reihenfolge neu bestimmen, Schritt einklemmen, Sichtbarkeit nachziehen
  refreshAssembly() {
    const a = this.assemblyMode
    if (!a) return
    a.order = assemblyOrder(this.doc.allParts())
    if (a.order.length === 0) return this.finishAssembly()
    a.step = Math.min(a.step, a.order.length)
    this.applyAssemblyStep()
  }

  // --- Messwerkzeug -----------------------------------------------------------

  toggleMeasure() {
    if (this.measuring) return this.stopMeasure()
    if (this.sketchMode) return
    this.exitPhoto()
    this.measuring = { elements: [] }
    this.pointer.measureHandler = (p) => this.measureTap(p)
    this.measureButtonTarget.classList.add("bg-amber-600", "text-white")
    if (this.hasMeasureHintTarget) this.measureHintTarget.classList.remove("hidden")
  }

  // Angetippte Stellen werden Elemente: Ecken/Mitten rasten als Punkt ein,
  // Kanten greifen als ganze Kante. Zwei Elemente → kontextabhängige Werte:
  // Punktpaar = Abstand + Deltas, mit Kante = Min-/Max-Abstand, zwei Kanten
  // zusätzlich der Winkel (Shapr3D-/Fusion-Muster)
  measureTap(p) {
    const m = this.measuring
    if (!m) return
    if (m.elements.length === 2) m.elements = []
    m.elements.push(pickMeasureElement(this.doc.allParts(), p, this.renderer.worldToleranceFor(20)))
    const [ e1, e2 ] = m.elements
    const result = e2 ? measureBetween(e1, e2) : null
    this.renderer.setMeasurement({
      elements: m.elements,
      line: result?.closest ?? null,
      text: result ? formatMeasureText(result) : null
    })
  }

  stopMeasure() {
    if (!this.measuring) return
    this.measuring = null
    this.pointer.measureHandler = null
    this.renderer.setMeasurement(null)
    this.measureButtonTarget.classList.remove("bg-amber-600", "text-white")
    if (this.hasMeasureHintTarget) this.measureHintTarget.classList.add("hidden")
  }

  // --- Mehrfachauswahl mit Batch-Aktionen ------------------------------------

  // Eigener Modus (Muster sketchBar): multiBar ersetzt die Aktionsleiste,
  // Taps toggeln Teile im Set (Guard in select()). Ende: „Fertig“ oder Escape.
  toggleMultiSelect() {
    if (this.multiSelect) return this.finishMultiSelect()
    if (this.sketchMode) return
    this.stopMeasure()
    this.setSubSelection(null)
    this.select(null)
    this.multiSelect = { ids: new Set() }
    this.actionBarTarget.classList.add("hidden")
    this.multiBarTarget.classList.remove("hidden")
    this.populateMultiMaterialSelect()
    this.updateMultiUi()
  }

  finishMultiSelect() {
    if (!this.multiSelect) return
    this.multiSelect = null
    this.renderer.setMultiHighlight([])
    this.multiBarTarget.classList.add("hidden")
    this.actionBarTarget.classList.remove("hidden")
    if (!this.partsPanelTarget.classList.contains("hidden")) this.renderPartsList()
  }

  toggleMultiPart(clientId) {
    const ids = this.multiSelect.ids
    if (ids.has(clientId)) ids.delete(clientId)
    else ids.add(clientId)
    this.updateMultiUi()
  }

  updateMultiUi() {
    if (!this.multiSelect) return
    const count = this.multiSelect.ids.size
    this.renderer.setMultiHighlight([ ...this.multiSelect.ids ])
    this.multiCountTarget.textContent = count === 1 ? "1 Teil" : `${count} Teile`
    if (!this.partsPanelTarget.classList.contains("hidden")) this.renderPartsList()
  }

  // Gewählte Teile in Dokument-Reihenfolge (verschwundene Ids fallen raus)
  multiParts() {
    const ids = this.multiSelect?.ids ?? new Set()
    return this.doc.allParts().filter((part) => ids.has(part.client_id))
  }

  populateMultiMaterialSelect() {
    const select = this.multiMaterialTarget
    select.replaceChildren()
    select.add(new Option("Material…", ""))
    for (const material of this.doc.materials.values()) {
      select.add(new Option(material.name, material.id))
    }
    select.value = ""
  }

  // Löschen aller gewählten Teile — ein Undo-Schritt
  multiDelete() {
    const parts = this.multiParts()
    if (parts.length === 0) return
    this.history.execute(new CompositeCommand(parts.map((part) => new RemovePartCommand(part.client_id))))
    this.multiSelect.ids.clear()
    this.updateMultiUi()
  }

  // Duplizieren mit Versatz +20/+20; Verweise werden in multiselect.js
  // remappt. Die Kopien wandern nicht automatisch in die Auswahl.
  multiDuplicate() {
    const parts = this.multiParts()
    if (parts.length === 0) return
    const copies = duplicatedParts(parts, { x: 20, y: 0, z: 20 })
    this.history.execute(new CompositeCommand(copies.map((part) => new AddPartCommand(part))))
  }

  multiApplyMaterial(event) {
    const raw = event.target.value
    event.target.value = ""
    if (raw === "") return
    const parts = this.multiParts()
    if (parts.length === 0) return
    this.history.execute(new UpdatePartsCommand(batchMaterialUpdates(parts, parseInt(raw, 10))))
  }

  // Verschieben um Delta (mm): X quer, H = Höhe (y), T = Tiefe (z)
  multiApplyMove() {
    const delta = {
      x: parseFloat(this.multiDxTarget.value) || 0,
      y: parseFloat(this.multiDyTarget.value) || 0,
      z: parseFloat(this.multiDzTarget.value) || 0
    }
    const parts = this.multiParts()
    if (parts.length === 0 || (delta.x === 0 && delta.y === 0 && delta.z === 0)) return
    this.history.execute(new UpdatePartsCommand(batchMoveUpdates(parts, delta)))
  }

  // --- Baugruppen (freie Gruppen über meta.group, siehe groups.js) -----------

  // Alle Mitglieder einer Baugruppe in Dokument-Reihenfolge
  groupMembers(groupId) {
    return this.doc.allParts().filter((part) => groupOf(part)?.id === groupId)
  }

  // Multi-Leiste: alle Gewählten (≥2) bekommen dieselbe frische Gruppe;
  // Teile aus anderen Gruppen wechseln dabei in die neue — ein Undo-Schritt
  groupSelection() {
    const parts = this.multiParts()
    if (parts.length < 2) return
    const group = { id: generateClientId(), name: nextGroupName(this.doc.allParts()) }
    this.history.execute(new CompositeCommand(parts.map((part) =>
      new UpdatePartCommand(part.client_id, { meta: withGroup(part, group) }))))
  }

  // Kontextleiste: Gruppe von ALLEN Mitgliedern entfernen — ein Undo-Schritt
  ungroup() {
    const part = this.doc.getPart(this.selectedId)
    const group = part ? groupOf(part) : null
    if (!group) return
    this.history.execute(new CompositeCommand(this.groupMembers(group.id).map((member) =>
      new UpdatePartCommand(member.client_id, { meta: withoutGroup(member) }))))
  }

  // --- Gruppe umbenennen: das Label wird kurzzeitig zum Textfeld -------------
  // (Muster: Skizzen-Umbenennen in der sketchBar)

  renameGroup() {
    const part = this.doc.getPart(this.selectedId)
    const group = part ? groupOf(part) : null
    if (!group) return
    this.groupNameInputTarget.value = group.name
    this.groupLabelTarget.classList.add("hidden")
    this.groupNameInputTarget.classList.remove("hidden")
    this.groupNameInputTarget.focus()
    this.groupNameInputTarget.select()
  }

  // Enter/Blur übernimmt (leer → alter Name bleibt); der Name wandert in
  // ALLE Mitglieder — ein CompositeCommand, damit die Gruppe konsistent bleibt
  applyGroupRename() {
    if (this.groupNameInputTarget.classList.contains("hidden")) return // Blur nach Enter/Escape
    const name = this.groupNameInputTarget.value.trim()
    this.closeGroupRename()
    const part = this.doc.getPart(this.selectedId)
    const group = part ? groupOf(part) : null
    if (!name || !group || name === group.name) return
    const renamed = { id: group.id, name }
    this.history.execute(new CompositeCommand(this.groupMembers(group.id).map((member) =>
      new UpdatePartCommand(member.client_id, { meta: withGroup(member, renamed) }))))
  }

  cancelGroupRename() {
    this.closeGroupRename()
  }

  closeGroupRename() {
    this.groupNameInputTarget.classList.add("hidden")
    this.refreshButtons() // Label wieder einblenden, falls weiterhin Gruppenmitglied
  }

  // --- Lineares Muster (Dialog-Muster wie cabinetDialog) ----------------------

  // Quelle des Musters: Multi-Auswahl, sonst die ganze Baugruppe des
  // selektierten Teils, sonst das Einzelteil
  patternSourceParts() {
    if (this.multiSelect) return this.multiParts()
    const part = this.doc.getPart(this.selectedId)
    if (!part) return []
    const group = groupOf(part)
    return group ? this.groupMembers(group.id) : [ part ]
  }

  // Gemerkte Quell-Ids wieder zu Teilen auflösen (verschwundene fallen raus)
  patternParts() {
    const ids = new Set(this.patternIds ?? [])
    return this.doc.allParts().filter((part) => ids.has(part.client_id))
  }

  openPatternDialog() {
    const parts = this.patternSourceParts()
    if (parts.length === 0) return
    this.patternIds = parts.map((part) => part.client_id)
    this.patternAxis = "x"
    this.patternCountTarget.value = 3
    this.patternSpacingTarget.value = defaultPatternSpacing(parts, this.patternAxis)
    this.patternHintTarget.textContent = parts.length === 1
      ? "1 Teil wird kopiert."
      : `${parts.length} Teile werden als Satz kopiert.`
    this.updatePatternAxisButtons()
    this.patternDialogTarget.classList.remove("hidden")
  }

  closePatternDialog() {
    this.patternIds = null
    this.patternDialogTarget.classList.add("hidden")
  }

  // Achswechsel setzt den Abstand auf die Vorgabe der neuen Achse zurück
  // (Teilmaß auf der Achse + 32 mm Luft)
  setPatternAxis(event) {
    this.patternAxis = event.params.axis
    this.patternSpacingTarget.value = defaultPatternSpacing(this.patternParts(), this.patternAxis)
    this.updatePatternAxisButtons()
  }

  updatePatternAxisButtons() {
    this.patternAxisButtonTargets.forEach((el) => {
      const active = el.dataset.editorAxisParam === this.patternAxis
      el.classList.toggle("bg-amber-600", active)
      el.classList.toggle("text-white", active)
      el.classList.toggle("bg-stone-700", !active)
    })
  }

  // N Kopien der Quelle, Kopie k um k×Abstand entlang der Achse versetzt —
  // Remap je Satz wie beim Duplizieren, alles ein Undo-Schritt
  createPattern() {
    const parts = this.patternParts()
    if (parts.length === 0) return this.closePatternDialog()
    const count = parseInt(this.patternCountTarget.value, 10)
    const spacing = parseInt(this.patternSpacingTarget.value, 10)
    if (!Number.isFinite(count) || count < 1 || count > 30) return
    if (!Number.isFinite(spacing) || spacing < 1) return
    const copies = patternCopies(parts, { count, axis: this.patternAxis, spacing })
    this.history.execute(new CompositeCommand(copies.map((part) => new AddPartCommand(part))))
    this.closePatternDialog()
  }

  // --- Lochreihe / System 32 (Dialog-Muster wie patternDialog) ---------------

  openHoleRowDialog() {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    this.holeRowPartId = part.client_id
    this.holeRowDirection = HOLE_ROW_DEFAULTS.direction
    this.holeRowCountTarget.value = HOLE_ROW_DEFAULTS.count
    this.holeRowPitchTarget.value = HOLE_ROW_DEFAULTS.pitch
    this.holeRowDiameterTarget.value = HOLE_ROW_DEFAULTS.diameter
    this.holeRowDepthTarget.value = HOLE_ROW_DEFAULTS.depth
    this.holeRowStartTarget.value = HOLE_ROW_DEFAULTS.startOffset
    this.holeRowEdgeTarget.value = HOLE_ROW_DEFAULTS.edgeOffset
    this.holeRowHintTarget.textContent =
      `${part.name}: ${part.length_mm} × ${part.width_mm} × ${part.thickness_mm} mm — ` +
      "Abstände ab Plattenkante zur Bohrungsmitte."
    this.updateHoleRowDirectionButtons()
    this.holeRowDialogTarget.classList.remove("hidden")
  }

  closeHoleRowDialog() {
    this.holeRowPartId = null
    this.holeRowDialogTarget.classList.add("hidden")
  }

  setHoleRowDirection(event) {
    this.holeRowDirection = event.params.direction
    this.updateHoleRowDirectionButtons()
  }

  updateHoleRowDirectionButtons() {
    this.holeRowDirectionButtonTargets.forEach((el) => {
      const active = el.dataset.editorDirectionParam === this.holeRowDirection
      el.classList.toggle("bg-amber-600", active)
      el.classList.toggle("text-white", active)
      el.classList.toggle("bg-stone-700", !active)
    })
  }

  // Die komplette Reihe hängt sich an die vorhandenen Bohrungen an und wandert
  // als EIN UpdatePartCommand ins Undo — ein Klick auf ↩ nimmt sie ganz zurück.
  createHoleRow() {
    const part = this.doc.getPart(this.holeRowPartId)
    if (!part) return this.closeHoleRowDialog()
    const bores = holeRowCutouts({
      count: numberField(this.holeRowCountTarget),
      pitch: numberField(this.holeRowPitchTarget),
      diameter: numberField(this.holeRowDiameterTarget),
      depth: numberField(this.holeRowDepthTarget),
      startOffset: numberField(this.holeRowStartTarget),
      edgeOffset: numberField(this.holeRowEdgeTarget),
      direction: this.holeRowDirection,
      part
    })
    if (bores.length > 0) {
      const cutouts = [ ...cutoutsOf(part), ...bores ]
      this.history.execute(new UpdatePartCommand(part.client_id, { meta: withCutouts(part, cutouts) }))
    }
    this.closeHoleRowDialog()
  }

  // --- Schnittansicht (rein visuell — wird weder gespeichert noch geundot) ---

  toggleSection() {
    if (this.sectionView) return this.stopSection()
    if (this.sketchMode) return
    const axis = "x"
    this.sectionView = { axis, flip: false, offsetMm: defaultSectionOffset(this.renderer.sectionRange(axis)) }
    this.sectionMenuTarget.classList.remove("hidden")
    this.sectionButtonTarget.classList.add("bg-amber-600", "text-white")
    this.applySection()
  }

  stopSection() {
    if (!this.sectionView) return
    this.sectionView = null
    this.renderer.setSectionPlane(null)
    this.sectionMenuTarget.classList.add("hidden")
    this.sectionButtonTarget.classList.remove("bg-amber-600", "text-white")
  }

  // Achswechsel startet in der Bereichsmitte — sonst stünde der alte
  // Versatz beliebig weit außerhalb des neuen Bereichs
  setSectionAxis(event) {
    const s = this.sectionView
    if (!s) return
    s.axis = event.params.axis
    s.offsetMm = defaultSectionOffset(this.renderer.sectionRange(s.axis))
    this.applySection()
  }

  flipSection() {
    const s = this.sectionView
    if (!s) return
    s.flip = !s.flip
    this.applySection()
  }

  setSectionOffset(event) {
    const s = this.sectionView
    if (!s) return
    s.offsetMm = clampSectionOffset(parseFloat(event.target.value), this.renderer.sectionRange(s.axis))
    this.applySection()
  }

  applySection() {
    const s = this.sectionView
    if (!s) return
    const range = this.renderer.sectionRange(s.axis)
    s.offsetMm = clampSectionOffset(s.offsetMm, range)
    this.renderer.setSectionPlane({ axis: s.axis, offsetMm: s.offsetMm, flip: s.flip })
    this.updateSectionUi(range)
  }

  updateSectionUi(range) {
    const s = this.sectionView
    this.sectionAxisTargets.forEach((el) => {
      const active = el.dataset.editorAxisParam === s.axis
      el.classList.toggle("bg-amber-600", active)
      el.classList.toggle("text-white", active)
      el.classList.toggle("bg-stone-700", !active)
    })
    this.sectionFlipTarget.classList.toggle("bg-amber-600", s.flip)
    this.sectionFlipTarget.classList.toggle("text-white", s.flip)
    const slider = this.sectionSliderTarget
    slider.min = range?.min ?? 0
    slider.max = range?.max ?? 0
    slider.value = s.offsetMm
    slider.disabled = !range
  }

  // --- Panel-Sektionen (Akkordeon, Zustand bleibt erhalten) ------------------

  restoreSections() {
    let stored = {}
    try { stored = JSON.parse(localStorage.getItem("plattenwerk-sections") || "{}") } catch { /* egal */ }
    this.sectionTargets.forEach((el) => {
      if (el.dataset.section in stored) el.open = stored[el.dataset.section]
    })
    this.sectionsRestored = true
  }

  sectionToggled(event) {
    if (!this.sectionsRestored) return
    let stored = {}
    try { stored = JSON.parse(localStorage.getItem("plattenwerk-sections") || "{}") } catch { /* egal */ }
    stored[event.target.dataset.section] = event.target.open
    localStorage.setItem("plattenwerk-sections", JSON.stringify(stored))
  }

  // --- Rechte Panels (gegenseitig exklusiv) -----------------------------------
  // Eine Stelle für alle fünf: jedes Öffnen und Schließen läuft hier durch,
  // damit nie zwei Panels denselben Platz beanspruchen. Die Toggles darunter
  // sind nur noch Namen für setSidePanel().

  sidePanelElement(name) {
    return this[`${SIDE_PANELS[name].panel}Target`]
  }

  sidePanelOpen(name) {
    return !this.sidePanelElement(name).classList.contains("hidden")
  }

  openSidePanel(name) {
    this.setSidePanel(name, true)
  }

  // Öffnen verdrängt die anderen; Schließen räumt den panel-eigenen Zustand auf.
  setSidePanel(name, open) {
    if (open) this.closeSidePanels(name)
    this.sidePanelElement(name).classList.toggle("hidden", !open)
    // Details: der Wunsch-Zustand muss mitlaufen, sonst käme das Panel bei der
    // nächsten Auswahl von selbst zurück und verdeckte das gerade Geöffnete
    if (name === "details") this.panelOpen = open
    if (open) this.fillSidePanel(name)
    // Der Filter der Teile-Liste ist an die offene Liste gebunden: zu = zurück
    else if (name === "parts") this.resetPartsFilter()
    this.refreshSidePanelButtons()
    return open
  }

  closeSidePanels(except = null) {
    for (const name of Object.keys(SIDE_PANELS)) {
      if (name === except || !this.sidePanelOpen(name)) continue
      this.setSidePanel(name, false)
    }
  }

  // Inhalt erst beim Öffnen erzeugen — die Panels sind sonst nur leere Hüllen
  fillSidePanel(name) {
    if (name === "parts") this.renderPartsList()
    else if (name === "templates") this.loadTemplates()
    else if (name === "variables") this.renderVariablesList()
    else if (name === "checks") this.renderChecks()
    else if (name === "details") this.fillPanel()
  }

  // Jeder Panel-Knopf zeigt, ob „sein“ Panel gerade zu sehen ist. Beim
  // Ablösen bliebe sonst eine Hervorhebung stehen und behauptete etwas,
  // das der Nutzer nicht sieht.
  refreshSidePanelButtons() {
    for (const [ name, { button } ] of Object.entries(SIDE_PANELS)) {
      if (!this[`has${button[0].toUpperCase()}${button.slice(1)}Target`]) continue
      const on = this.sidePanelOpen(name)
      const element = this[`${button}Target`]
      element.classList.toggle("bg-amber-600", on)
      element.classList.toggle("text-white", on)
      element.setAttribute("aria-pressed", String(on))
    }
  }

  // --- Teile-Liste ------------------------------------------------------------

  togglePartsList() {
    this.setSidePanel("parts", !this.sidePanelOpen("parts"))
  }

  // --- Filter der Teile-Liste (reiner UI-Zustand) -----------------------------

  setPartsQuery(event) {
    this.partsFilter = { ...this.partsFilter, query: event.target.value }
    this.renderPartsList()
  }

  togglePartsOnlyVisible() {
    this.partsFilter = { ...this.partsFilter, onlyVisible: !this.partsFilter.onlyVisible }
    this.renderPartsList()
  }

  resetPartsFilter() {
    this.partsFilter = { query: "", onlyVisible: false }
    if (this.hasPartsSearchTarget) this.partsSearchTarget.value = ""
    this.updatePartsFilterUi(0, 0)
  }

  // Trefferzahl im Panel-Kopf + Zustand des „nur sichtbare“-Umschalters
  updatePartsFilterUi(shown, total) {
    const active = isFilterActive(this.partsFilter)
    if (this.hasPartsFilterCountTarget) {
      this.partsFilterCountTarget.textContent = active ? `${shown} von ${total}` : `(${total})`
    }
    if (this.hasPartsOnlyVisibleButtonTarget) {
      const on = this.partsFilter.onlyVisible
      this.partsOnlyVisibleButtonTarget.setAttribute("aria-pressed", String(on))
      this.partsOnlyVisibleButtonTarget.classList.toggle("bg-amber-600", on)
      this.partsOnlyVisibleButtonTarget.classList.toggle("text-white", on)
      this.partsOnlyVisibleButtonTarget.classList.toggle("bg-stone-700", !on)
    }
  }

  renderPartsList() {
    const list = this.partsListTarget
    list.replaceChildren()
    const all = this.doc.allParts()
    // „Alle einblenden“ nur zeigen, wenn mindestens ein Teil versteckt ist
    if (this.hasShowAllButtonTarget) {
      this.showAllButtonTarget.classList.toggle("hidden", !all.some((part) => isHidden(part)))
    }
    const parts = filterParts(all, this.partsFilter ?? {},
      (part) => this.doc.getMaterial(part.material_id)?.name ?? "")
    this.updatePartsFilterUi(parts.length, all.length)
    if (parts.length === 0) {
      const empty = document.createElement("p")
      empty.className = "text-xs text-stone-400 py-2"
      empty.textContent = all.length === 0 ? "Noch keine Teile." : "Kein Teil passt zum Filter."
      list.appendChild(empty)
      return
    }
    for (const part of parts) {
      const hidden = isHidden(part)
      const active = this.multiSelect
        ? this.multiSelect.ids.has(part.client_id)
        : part.client_id === this.selectedId
      // Zeile = div mit zwei Buttons (Zeilen-Button + Augen-Button) —
      // verschachtelte Buttons wären invalides HTML
      const row = document.createElement("div")
      row.className = "flex items-stretch rounded-lg border " +
        (active ? "border-amber-500 bg-stone-700/60" : "border-stone-600 hover:border-stone-400")
      const rowButton = document.createElement("button")
      rowButton.className = "flex-1 min-w-0 text-left px-3 py-2" + (hidden ? " opacity-40" : "")
      const title = document.createElement("div")
      title.className = "flex items-center gap-1.5 text-sm text-stone-100"
      const material = this.doc.getMaterial(part.material_id)
      if (material) {
        const dot = document.createElement("span")
        dot.className = "w-2.5 h-2.5 rounded-full border border-stone-500 shrink-0"
        dot.style.background = material.color
        dot.title = material.name
        title.appendChild(dot)
      }
      const label = document.createElement("span")
      label.className = "truncate"
      label.textContent = part.name
      title.appendChild(label)
      const dims = document.createElement("div")
      dims.className = "text-[10px] text-stone-400"
      dims.textContent = `${part.length_mm} × ${part.width_mm} × ${part.thickness_mm} mm`
      // Maserrichtung dezent als Pfeil-Kürzel hinter den Maßen
      const grain = grainOf(part)
      if (grain) {
        const mark = document.createElement("span")
        mark.className = "ml-1 text-stone-300"
        mark.textContent = grainSymbol(grain)
        mark.title = `Maserung ${grainLabel(grain)}`
        dims.appendChild(mark)
      }
      rowButton.append(title, dims)
      rowButton.addEventListener("click", () => {
        if (hidden && !this.multiSelect) return // unsichtbar wird nicht selektiert — erst einblenden
        this.select(part.client_id) // Multi-Modus: toggelt nur die Zugehörigkeit
        if (this.multiSelect) return
        this.renderer.focusOnPart(part.client_id)
        // gezielt schließen statt toggeln: select() kann die Liste schon
        // verdrängt haben (Details), ein Toggle risse sie dann wieder auf
        this.setSidePanel("parts", false)
      })
      const eye = document.createElement("button")
      eye.className = "shrink-0 px-2 flex items-center " +
        (hidden ? "text-stone-500 hover:text-stone-300" : "text-stone-400 hover:text-white")
      eye.innerHTML = eyeSvg(hidden)
      eye.title = hidden ? "Einblenden" : "Ausblenden"
      eye.setAttribute("aria-label", `${part.name} ${hidden ? "einblenden" : "ausblenden"}`)
      eye.addEventListener("click", () => this.togglePartHidden(part.client_id))
      row.append(rowButton, eye)
      list.appendChild(row)
    }
  }

  // --- Teile ausblenden & isolieren ------------------------------------------

  // Augen-Toggle einer Zeile — über UpdatePartCommand undo-fähig,
  // markDirty läuft über documentChanged automatisch
  togglePartHidden(clientId) {
    const part = this.doc.getPart(clientId)
    if (!part) return
    this.history.execute(new UpdatePartCommand(clientId, { meta: withHidden(part, !isHidden(part)) }))
  }

  // h-Shortcut: Ausblenden-Toggle der Selektion (auch Mehrfachauswahl);
  // gemischter Zustand blendet erst alles aus, der zweite Druck wieder ein
  toggleHiddenSelection() {
    const parts = this.multiSelect
      ? this.multiParts()
      : [ this.doc.getPart(this.selectedId) ].filter(Boolean)
    if (parts.length === 0) return
    const hide = !parts.every((part) => isHidden(part))
    const commands = parts
      .filter((part) => isHidden(part) !== hide)
      .map((part) => new UpdatePartCommand(part.client_id, { meta: withHidden(part, hide) }))
    if (commands.length === 0) return
    this.history.execute(commands.length === 1 ? commands[0] : new CompositeCommand(commands))
  }

  // Kontextleiste: alle NICHT zur Auswahl gehörenden Teile ausblenden —
  // ein Undo-Schritt (CompositeCommand aus UpdatePartCommands)
  isolateSelection() {
    const keepIds = this.multiSelect && this.multiSelect.ids.size > 0
      ? [ ...this.multiSelect.ids ]
      : (this.selectedId ? [ this.selectedId ] : [])
    if (keepIds.length === 0) return
    this.executeVisibilityUpdates(isolateUpdates(this.doc.allParts(), keepIds))
  }

  // „Alle einblenden“ in der Teile-Liste
  showAllParts() {
    this.executeVisibilityUpdates(showAllUpdates(this.doc.allParts()))
  }

  executeVisibilityUpdates(updates) {
    if (updates.length === 0) return
    this.history.execute(new CompositeCommand(
      updates.map(({ clientId, attrs }) => new UpdatePartCommand(clientId, attrs))
    ))
  }

  // --- Gewicht & Schwerpunkt --------------------------------------------------

  // Gesamtgewicht aus den Rohdichten der Materialien; Teile ohne Rohdichte
  // werden gezählt statt geschätzt. Hängt an updatePartsCount(), läuft also
  // beim Laden und nach jeder Dokumentänderung mit.
  // Gerechnet wird über ALLE Teile — auch ausgeblendete, wie in Stückliste und
  // Zuschnittplan. Dass sie mitzählen, steht in der Zeile (weightSummary).
  updateWeight() {
    if (!this.hasWeightSummaryTarget) return
    const parts = this.doc.allParts()
    this.weightSummaryTarget.textContent = weightSummary(parts, this.doc.materials)
    this.weightSummaryTarget.title = hiddenCount(parts) > 0
      ? "Ausgeblendete Teile werden mitgerechnet — gebaut wird, was im Projekt steht"
      : ""
    this.updateCenterOfMass()
  }

  // Schwerpunkt-Lot ein-/ausblenden (gleicher Ort wie das Gewicht)
  toggleCenterOfMass() {
    this.centerOfMassOn = !this.centerOfMassOn
    const on = this.centerOfMassOn
    this.comButtonTarget.setAttribute("aria-pressed", String(on))
    this.comButtonTarget.classList.toggle("bg-sky-600", on)
    this.comButtonTarget.classList.toggle("text-white", on)
    this.comButtonTarget.classList.toggle("bg-stone-700", !on)
    this.updateCenterOfMass()
  }

  // Marker im 3D-Raum + Kippgefahr-Hinweis: liegt das Lot des Schwerpunkts
  // außerhalb der Standfläche (bodennahe Teile), steht das Möbel nicht sicher.
  // Grundmenge ist dieselbe wie bei der Gewichtsanzeige — alle Teile, auch
  // ausgeblendete. Sonst spränge der Marker beim Isolieren einer Baugruppe,
  // obwohl sich am Möbel nichts geändert hat.
  updateCenterOfMass() {
    if (!this.hasTippingHintTarget || !this.renderer) return
    const risk = this.centerOfMassOn ? tippingRisk(this.doc.allParts(), this.doc.materials) : null
    this.renderer.setCenterOfMass(risk ? { ...risk.com, stable: risk.stable } : null)
    const warn = Boolean(risk) && !risk.stable
    this.tippingHintTarget.classList.toggle("hidden", !warn)
    if (warn) {
      this.tippingHintTarget.textContent = " · ⚠ Kippgefahr"
      this.tippingHintTarget.title =
        `Schwerpunkt liegt ${Math.round(-risk.marginMm)} mm außerhalb der Standfläche — Möbel an der Wand sichern`
    }
  }

  updatePartsCount() {
    this.updateWeight()
    this.partsCountTarget.textContent = `(${this.doc.allParts().length})`
    // Einstiegs-Hinweis nur im wirklich leeren Projekt (kein Teil, keine Skizze)
    if (this.hasEmptyStateTarget) {
      const empty = this.doc.allParts().length === 0 && !this.doc.hasSketchShapes() && !this.sketchMode
      this.emptyStateTarget.classList.toggle("hidden", !empty)
    }
  }

  doubleTapped(clientId) {
    if (clientId) {
      this.select(clientId)
      this.renderer.focusOnPart(clientId)
    } else {
      this.renderer.frameAll()
    }
  }

  // --- Ecken-/Kanten-Auswahl (Kontextleiste) ---------------------------------

  setSubSelection(sub) {
    this.subSelection = sub
    this.renderer.setSubSelected(sub)
    this.renderSubBar()
  }

  renderSubBar() {
    const sub = this.subSelection
    const part = this.doc.getPart(this.selectedId)
    const visible = Boolean(sub && part)
    this.subBarTarget.classList.toggle("hidden", !visible)
    if (!visible) return

    const actions = this.subBarActionsTarget
    actions.replaceChildren()
    const button = (label, active, fn) => {
      const el = document.createElement("button")
      el.className = "min-h-10 rounded-lg px-3 text-sm whitespace-nowrap " +
        (active ? "bg-amber-600 text-white" : "bg-stone-700 text-stone-100 hover:bg-stone-600")
      el.textContent = label
      el.addEventListener("click", fn)
      return el
    }

    if (sub.type === "corner") {
      this.subBarLabelTarget.textContent = `Ecke ${sub.key.toUpperCase()}`
      const current = cornersOf(part)[sub.key]
      const size = document.createElement("input")
      size.type = "number"
      size.inputMode = "numeric"
      size.min = "1"
      size.value = current?.size_mm ?? 20
      size.className = "w-16 rounded-lg bg-stone-900 border border-stone-600 px-2 py-2 text-sm text-center"
      size.addEventListener("change", () => {
        if (current) this.applyCornerFromBar(current.type, size.value)
      })
      actions.append(
        button("Scharf", !current, () => this.applyCornerFromBar(null, size.value)),
        button("Fase", current?.type === "chamfer", () => this.applyCornerFromBar("chamfer", size.value)),
        button("Radius", current?.type === "round", () => this.applyCornerFromBar("round", size.value)),
        size
      )
    } else {
      this.subBarLabelTarget.textContent = `Kante ${sub.key.toUpperCase()}`
      const banded = edgesOf(part).includes(sub.key)
      const mitered = mitersOf(part).some((miter) => miter.edge === sub.key)
      actions.append(
        button("Umleimer", banded, () =>
          this.history.execute(new UpdatePartCommand(this.selectedId, { meta: toggleEdge(part, sub.key) }))),
        button("+ Falz", false, () =>
          this.appendGroove({ type: "falz", edge: sub.key, width_mm: 12, depth_mm: 9 })),
        button("+ Nut", false, () =>
          this.appendGroove(this.nutTemplateForEdge(sub.key, part))),
        button("Gehrung 45°", mitered, () =>
          this.history.execute(new UpdatePartCommand(this.selectedId, { meta: withMiter(part, sub.key) }))),
        button("Ausrichten…", false, () => this.startPick("edge-align"))
      )
    }
  }

  applyCornerFromBar(type, sizeRaw) {
    const part = this.doc.getPart(this.selectedId)
    const sub = this.subSelection
    if (!part || !sub || sub.type !== "corner") return
    const treatment = type ? { type, size_mm: parseFloat(sizeRaw) } : null
    this.history.execute(new UpdatePartCommand(this.selectedId, { meta: withCorner(part, sub.key, treatment) }))
  }

  // Nut parallel zur getippten Kante, 30 mm Abstand (Kante → Nutmitte)
  nutTemplateForEdge(key, part) {
    const along = key.startsWith("l") ? "x" : "z"
    const cross = along === "x" ? part.width_mm : part.length_mm
    const offset = key === "l1" || key === "b1" ? 30 : cross - 30
    return { type: "nut", direction: along, width_mm: 8, depth_mm: 8, offset_mm: offset }
  }

  disconnect() {
    if (window.__editor === this) delete window.__editor
    clearTimeout(this.warningTimer)
    removeEventListener("keydown", this.keydownBound)
    this.unsubscribe?.()
    // Präsenz sauber abmelden — sonst steht man bis zum Ablauf der TTL (40 s)
    // noch bei den anderen im Header
    this.collab?.dispose()
    this.collab = null
    this.sync?.dispose()
    this.renderer?.dispose()
    this.renderer = null
  }

  documentChanged(event) {
    this.renderer.syncScene(this.doc)
    if (event.type !== "loaded") this.sync.markDirty()
    else {
      this.renderSketch() // gespeicherte Skizze nach dem Laden dezent anzeigen
      this.renderer.setRoom(this.doc.room) // persistiertes Raummaß wiederherstellen
    }
    // Variablen haben sich geändert (auch per Undo/Redo) — offenes Panel und
    // die Bindungs-Selects im Details-Panel nachziehen
    if (event.type === "variables:changed") {
      if (this.hasVariablesPanelTarget && !this.variablesPanelTarget.classList.contains("hidden")) {
        this.renderVariablesList()
      }
      if (this.selectedId) this.fillBindSelects(this.doc.getPart(this.selectedId))
    }
    this.scheduleWarningCheck()
    // Gekoppelte Skizzier-Ebenen folgen ihrem Teil — Visuals nachziehen
    if ((event.type === "part:updated" || event.type === "part:removed") &&
        this.doc.sketches.some((sk) => sk.plane?.ref?.client_id === event.clientId)) {
      this.renderSketch()
      this.updateSketchUi()
    }
    if (event.clientId === this.selectedId) {
      if (this.doc.getPart(this.selectedId)) this.fillPanel()
      else this.select(null)
    } else if (this.selectedId && this.doc.getPart(this.selectedId)) {
      // Die Auflager eines Bodens stecken in ANDEREN Teilen: ein eingebauter
      // Mittelsteg oder eine verschobene Seite ändert seine Durchbiegung
      this.renderDeflection(this.doc.getPart(this.selectedId))
    }
    // Selektiertes Teil wurde ausgeblendet (Auge, h, Isolieren, Undo …):
    // Selektion aufheben — unsichtbare Teile tragen keine Handles
    if (this.selectedId && isHidden(this.doc.getPart(this.selectedId))) this.select(null)
    this.refreshButtons()
    this.refreshRelationLines()
    this.updatePartsCount()
    if (this.sectionView) this.applySection() // Slider-Bereich folgt neuen Bounds
    this.refreshAssembly()
    if (!this.partsPanelTarget.classList.contains("hidden")) this.renderPartsList()
  }

  // Kanten-Resize aus dem 3D-Raum: Maße + Position in einem Command,
  // Bearbeitungen (Ausschnitte/Ecken/Nuten) werden mitgeklemmt.
  // Wie beim Drehen ändert sich die Welt-AABB des Teils, deshalb wird auch
  // seine EIGENE Sollposition aus den Constraints neu gelöst (gleicher
  // Undo-Schritt) — der fixierte Flächenabstand gilt unabhängig von der Größe.
  resizePart(clientId, attrs) {
    const part = this.doc.getPart(clientId)
    if (!part) return
    // Ein an eine Variable gebundenes Maß kommt ausschließlich von dort —
    // Kantenziehen darf es nicht hinter dem Rücken der Variablen verstellen
    if (BINDABLE_FIELDS.some((field) => field in attrs && boundName(part, field))) {
      this.notify("Maß ist an eine Variable gebunden")
      return
    }
    const meta = this.resizedMeta(part, { ...part, ...attrs })
    const resized = meta ? { ...attrs, meta } : { ...attrs }
    const ownPos = solveFor({ ...part, ...resized }, this.doc.allParts())
    this.executeWithConstraints(clientId, ownPos ? { ...resized, ...ownPos } : resized)
  }

  // Drehung ausführen: Die Welt-AABB des Teils ändert sich (eine gekippte Platte
  // ist plötzlich 600 statt 19 mm hoch), also müssen Abstands-Constraints wie bei
  // Verschieben/Resize im SELBEN Undo-Schritt nachgeführt werden. Hat das gedrehte
  // Teil selbst Constraints, wird auch seine eigene Sollposition neu gelöst — der
  // fixierte Flächenabstand gilt unabhängig von der Drehung.
  // Bewusst NICHT mitgedreht werden geheftete Teile (meta.attached_to): Heften
  // nimmt Teile nur beim Verschieben mit; ein Mitrotieren um den Anker wäre eine
  // Verhaltensänderung und bleibt hier außen vor.
  rotatePart(clientId, attrs) {
    const part = this.doc.getPart(clientId)
    if (!part) return
    const ownPos = solveFor({ ...part, ...attrs }, this.doc.allParts())
    this.executeWithConstraints(clientId, ownPos ? { ...attrs, ...ownPos } : attrs)
  }

  // Update ausführen; Teile mit Abstands-Constraints auf das geänderte Teil
  // werden im selben Undo-Schritt nachgeführt (kaskadierend)
  executeWithConstraints(clientId, attrs) {
    const followers = followerUpdates(this.doc.allParts(), clientId, attrs)
    this.history.execute(followers.length === 0
      ? new UpdatePartCommand(clientId, attrs)
      : new UpdatePartsCommand([ { clientId, attrs }, ...followers ]))
  }

  // Klemm-Logik liegt in editor/resize.js, damit auch die parametrischen
  // Variablen (editor/variables.js) exakt dieselbe Mechanik nutzen
  resizedMeta(part, resized) {
    return clampedMeta(part, resized)
  }

  // --- Bewegung mit gehefteten Teilen und Baugruppen -------------------------

  // Alles, was eine Bewegung von clientId mitmacht: transitiv Geheftete plus —
  // ist das Teil Baugruppen-Mitglied — alle Mitglieder samt deren Gehefteten.
  // Die Baugruppe wirkt als Erweiterung der Korpusgruppe: Korpusteile hängen
  // ohnehin am Anker (attached_to), die Gruppe vereinigt beide Mengen.
  moveCompanionIds(clientId) {
    const ids = new Set(descendantIdsOf(this.doc, clientId))
    const part = this.doc.getPart(clientId)
    const group = part ? groupOf(part) : null
    if (group) {
      for (const memberId of groupMemberIds(this.doc.allParts(), group.id)) {
        if (memberId === clientId) continue
        ids.add(memberId)
        for (const id of descendantIdsOf(this.doc, memberId)) ids.add(id)
      }
    }
    ids.delete(clientId)
    return [ ...ids ]
  }

  previewMove(clientId, pos) {
    const part = this.doc.getPart(clientId)
    if (!part) return
    const delta = { x: pos.pos_x - part.pos_x, y: pos.pos_y - part.pos_y, z: pos.pos_z - part.pos_z }
    this.renderer.setPreviewPosition(clientId, pos)
    for (const id of this.moveCompanionIds(clientId)) {
      const follower = this.doc.getPart(id)
      this.renderer.setPreviewPosition(id, {
        pos_x: follower.pos_x + delta.x, pos_y: follower.pos_y + delta.y, pos_z: follower.pos_z + delta.z
      })
    }
  }

  // Positionsänderung, die geheftete Teile und Baugruppen-Mitglieder mitnimmt
  // — ein Undo-Schritt; Abstands-Constraints werden mit nachgeführt
  movePart(clientId, attrs) {
    const part = this.doc.getPart(clientId)
    if (!part) return
    const delta = {
      x: (attrs.pos_x ?? part.pos_x) - part.pos_x,
      y: (attrs.pos_y ?? part.pos_y) - part.pos_y,
      z: (attrs.pos_z ?? part.pos_z) - part.pos_z
    }
    const updates = [{ clientId, attrs }]
    for (const id of this.moveCompanionIds(clientId)) {
      const follower = this.doc.getPart(id)
      updates.push({ clientId: id, attrs: {
        pos_x: follower.pos_x + delta.x, pos_y: follower.pos_y + delta.y, pos_z: follower.pos_z + delta.z
      } })
    }
    updates.push(...cascadeUpdates(this.doc.allParts(), updates))
    this.history.execute(updates.length === 1
      ? new UpdatePartCommand(clientId, attrs)
      : new UpdatePartsCommand(updates))
  }

  // --- Beziehungen: Ausrichten und Heften -----------------------------------

  PICK_HINTS = {
    "align": "Referenzteil im 3D-Raum antippen — Tipp ins Leere bricht ab.",
    "attach": "Referenzteil im 3D-Raum antippen — Tipp ins Leere bricht ab.",
    "distribute-a": "Erstes Grenzteil antippen (z. B. Unterboden).",
    "distribute-b": "Zweites Grenzteil antippen (z. B. Deckel).",
    "edge-align": "Teil antippen, das bündig an diese Kante soll.",
    "join": "Zweites Teil antippen — beide bekommen Verbinder-Bohrungen.",
    "constraint": "Bezugsteil antippen — der aktuelle Flächenabstand wird fixiert."
  }

  startAlignPick() { this.startPick("align") }
  startAttachPick() { this.startPick("attach") }
  startDistributePick() { this.startPick("distribute-a") }
  startJoinPick() { this.startPick("join") }
  startConstraintPick() { this.startPick("constraint") }

  startPick(mode) {
    if (!this.selectedId) return
    this.pickMode = mode
    this.pointer.pickHandler = (clientId) => this.referencePicked(clientId)
    this.pickHintTarget.textContent = this.PICK_HINTS[mode]
    this.pickHintTarget.classList.remove("hidden")
  }

  cancelPick() {
    this.pickMode = null
    this.pointer.pickHandler = null
    if (this.hasPickHintTarget) this.pickHintTarget.classList.add("hidden")
  }

  referencePicked(clientId) {
    const mode = this.pickMode
    if (!this.selectedId || clientId === this.selectedId) return this.cancelPick()

    if (mode === "align") {
      this.cancelPick()
      this.alignRefId = clientId
      this.relationRefNameTarget.textContent = this.doc.getPart(clientId)?.name ?? "?"
      this.relationBarTarget.classList.remove("hidden")
      this.renderer.setReferenceHighlight(clientId)
    } else if (mode === "attach") {
      this.cancelPick()
      if (wouldCreateCycle(this.doc, this.selectedId, clientId)) {
        this.notify("Nicht möglich: Kreis-Verbindung")
        return
      }
      const part = this.doc.getPart(this.selectedId)
      this.history.execute(new UpdatePartCommand(this.selectedId, { meta: withAnchor(part, clientId) }))
    } else if (mode === "join") {
      this.cancelPick()
      this.applyJoin(clientId)
    } else if (mode === "edge-align") {
      this.cancelPick()
      const sub = this.subSelection
      const source = this.doc.getPart(this.selectedId)
      const target = this.doc.getPart(clientId)
      if (!sub || sub.type !== "edge" || !source || !target) return
      const { field, delta } = alignToEdgeDelta(target, source, sub.key)
      this.movePart(clientId, { [field]: target[field] + delta })
    } else if (mode === "constraint") {
      this.cancelPick()
      this.addConstraintTo(clientId)
    } else if (mode === "distribute-a") {
      this.distributeAId = clientId
      this.startPick("distribute-b")
    } else if (mode === "distribute-b") {
      if (clientId === this.distributeAId) return // gleiches Grenzteil — weiter warten
      this.cancelPick()
      this.distributeBId = clientId
      this.distributeSectionTarget.classList.remove("hidden")
    }
  }

  applyDistribute() {
    const part = this.doc.getPart(this.selectedId)
    const boundaryA = this.doc.getPart(this.distributeAId)
    const boundaryB = this.doc.getPart(this.distributeBId)
    if (!part || !boundaryA || !boundaryB) return this.finishDistribute()

    const count = parseInt(this.distributeCountTarget.value, 10)
    if (!Number.isFinite(count) || count < 1 || count > 50) return

    const result = distributePositions(part, boundaryA, boundaryB, count)
    if (!result) {
      this.notify("Passt nicht: zu wenig Platz zwischen den Grenzteilen")
      return
    }

    const commands = [ new UpdatePartCommand(part.client_id, { [result.field]: result.positions[0] }) ]
    for (let i = 1; i < count; i++) {
      commands.push(new AddPartCommand(newPart({
        ...structuredClone(part), client_id: undefined, [result.field]: result.positions[i]
      })))
    }
    this.history.execute(new CompositeCommand(commands))
    this.finishDistribute()
  }

  finishDistribute() {
    this.distributeAId = null
    this.distributeBId = null
    if (this.hasDistributeSectionTarget) this.distributeSectionTarget.classList.add("hidden")
  }

  // --- Verbindungs-Assistent: symmetrische Verbinder-Bohrungen auf beiden Teilen

  setJoinType(event) {
    this.joinType = event.params.joinType
    this.updateJoinTypeButtons()
  }

  updateJoinTypeButtons() {
    this.joinTypeButtonTargets.forEach((el) => {
      const active = el.dataset.editorJoinTypeParam === this.joinType
      el.classList.toggle("bg-amber-600", active)
      el.classList.toggle("text-white", active)
      el.classList.toggle("text-stone-300", !active)
    })
  }

  applyJoin(otherId) {
    const partA = this.doc.getPart(this.selectedId)
    const partB = this.doc.getPart(otherId)
    if (!partA || !partB) return

    const contact = jointContact(partA, partB)
    if (!contact) {
      this.notify(jointIssueMessage(partA, partB))
      return
    }
    // Vorhandene Verbindung derselben zwei Teile erneuern statt verdoppeln:
    // erst deren Bohrungen abräumen, dann auf den bereinigten Teilen neu
    // verteilen (sonst blockierten die alten Bohrungen die neuen Punkte).
    const jointId = jointIdFor(partA, partB)
    const keptA = cutoutsWithoutJoint(partA, jointId)
    const keptB = cutoutsWithoutJoint(partB, jointId)
    const replaced = keptA.length !== cutoutsOf(partA).length
    const cleanA = { ...partA, meta: { ...partA.meta, cutouts: keptA } }
    const cleanB = { ...partB, meta: { ...partB.meta, cutouts: keptB } }

    const positions = jointPositions(contact)
    const bores = jointCutouts(cleanA, cleanB, this.joinType, positions)
    if (!bores) {
      this.notify(jointIssueMessage(partA, partB))
      return
    }
    // alle Bohrpunkte kollidieren mit Vorhandenem → nichts setzen, aber melden
    if (bores.a.length === 0) {
      this.notify(joinSummary(bores))
      return
    }

    // beide Teile in einem Undo-Schritt
    this.history.execute(new UpdatePartsCommand([
      { clientId: partA.client_id, attrs: { meta: withCutouts(partA, [ ...keptA, ...bores.a ]) } },
      { clientId: partB.client_id, attrs: { meta: withCutouts(partB, [ ...keptB, ...bores.b ]) } }
    ]))
    this.notify(joinSummary({ ...bores, replaced }))
  }

  align(event) {
    const part = this.doc.getPart(this.selectedId)
    const reference = this.doc.getPart(this.alignRefId)
    if (!part || !reference) return this.finishAlign()
    const { field, delta } = alignDelta(part, reference, event.params.mode)
    this.movePart(this.selectedId, { [field]: part[field] + delta })
  }

  // Heften aus der Beziehungs-Leiste: nutzt das bereits gewählte Referenzteil
  attachToReference() {
    const part = this.doc.getPart(this.selectedId)
    const refId = this.alignRefId
    if (!part || !refId || !this.doc.getPart(refId)) return this.finishAlign()
    if (wouldCreateCycle(this.doc, this.selectedId, refId)) {
      this.notify("Nicht möglich: Kreis-Verbindung")
      return
    }
    this.history.execute(new UpdatePartCommand(this.selectedId, { meta: withAnchor(part, refId) }))
    this.finishAlign()
    this.refreshRelationLines()
  }

  finishAlign() {
    this.alignRefId = null
    if (this.hasRelationBarTarget) this.relationBarTarget.classList.add("hidden")
    this.renderer.setReferenceHighlight(null)
  }

  detach() {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    this.history.execute(new UpdatePartCommand(this.selectedId, { meta: withAnchor(part, null) }))
  }

  // --- Abstands-Constraints -------------------------------------------------

  // Constraint auf der Achse mit dem kleinsten Flächenabstand anlegen;
  // ein bestehender Constraint auf derselben Achse wird ersetzt
  addConstraintTo(referenceId) {
    const part = this.doc.getPart(this.selectedId)
    const reference = this.doc.getPart(referenceId)
    if (!part || !reference) return
    const best = [ "x", "y", "z" ]
      .map((axis) => constraintFromCurrent(part, reference, axis))
      .reduce((a, b) => Math.abs(b.gap_mm) < Math.abs(a.gap_mm) ? b : a)
    const constraints = [ ...constraintsOf(part).filter((c) => c.axis !== best.axis), best ]
    this.history.execute(new UpdatePartCommand(this.selectedId, { meta: withConstraints(part, constraints) }))
  }

  renderConstraints(part) {
    const list = this.constraintListTarget
    list.replaceChildren()
    constraintsOf(part).forEach((constraint, index) => list.appendChild(this.buildConstraintRow(constraint, index)))
  }

  buildConstraintRow(constraint, index) {
    const row = document.createElement("div")
    row.className = "flex items-center gap-1 rounded-lg border border-stone-600 px-2 py-1"
    const label = document.createElement("span")
    label.className = "flex-1 min-w-0 truncate text-xs text-stone-200"
    const referenceName = this.doc.getPart(constraint.other)?.name ?? "(gelöschtes Teil)"
    label.textContent = `${constraint.axis.toUpperCase()} zu „${referenceName}“`
    label.title = `Flächenabstand auf der ${constraint.axis.toUpperCase()}-Achse zu „${referenceName}“`
    const input = document.createElement("input")
    input.type = "number"
    input.inputMode = "numeric"
    input.value = constraint.gap_mm
    input.className = "w-16 rounded bg-stone-900 border border-stone-600 px-1 py-1.5 text-stone-100 text-xs text-center"
    input.addEventListener("change", (event) => this.applyConstraintGap(index, event.target.value))
    const remove = document.createElement("button")
    remove.className = "text-xs text-stone-400 hover:text-red-400 min-h-8 px-2"
    remove.textContent = "✕"
    remove.title = "Abstand lösen"
    remove.addEventListener("click", () => this.removeConstraint(index))
    row.append(label, input, remove)
    return row
  }

  // Geänderter Abstand führt das Teil sofort nach — meta + Position in einem
  // Command; eigene Abhängige kaskadieren im selben Undo-Schritt mit
  applyConstraintGap(index, raw) {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    const value = parseInt(raw, 10)
    if (!Number.isFinite(value)) return this.fillPanel()
    const constraints = constraintsOf(part).map((c, i) => i === index ? { ...c, gap_mm: value } : c)
    const meta = withConstraints(part, constraints)
    const attrs = solveFor({ ...part, meta }, this.doc.allParts()) ?? {}
    this.executeWithConstraints(this.selectedId, { meta, ...attrs })
  }

  removeConstraint(index) {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    const constraints = constraintsOf(part).filter((_, i) => i !== index)
    this.history.execute(new UpdatePartCommand(this.selectedId, { meta: withConstraints(part, constraints) }))
  }

  // --- Selektion und Panel --------------------------------------------------

  select(clientId) {
    if (this.photoMode && clientId) return // Foto-Modus: Präsentation, keine Selektion
    // Multi-Modus: Tap toggelt die Zugehörigkeit statt einer Einzel-Selektion;
    // Tap ins Leere lässt die Mehrfachauswahl unverändert stehen
    if (this.multiSelect) {
      if (clientId) this.toggleMultiPart(clientId)
      return
    }
    if (this.assemblyMode) clientId = null // Montage-Modus ist reine Ansicht
    this.cancelPick()
    this.finishAlign()
    this.finishDistribute()
    this.closeViewsMenu()
    if (this.subSelection) this.setSubSelection(null)
    // Offenes Gruppen-Umbenennen gilt nur für die alte Selektion — schließen
    if (this.hasGroupNameInputTarget) this.groupNameInputTarget.classList.add("hidden")
    this.selectedId = clientId
    this.renderer.setSelected(clientId)
    this.renderer.setGroupHighlight(this.linkedPartIdsFor(clientId))
    // Details folgen der Auswahl, verdrängen dabei aber die anderen rechten
    // Panels. Ohne Auswahl wird nur ausgeblendet — der Wunsch (panelOpen) bleibt,
    // damit die nächste Auswahl die Details wieder mitbringt.
    const showDetails = Boolean(clientId) && this.panelOpen
    if (showDetails) this.closeSidePanels("details")
    this.panelTarget.classList.toggle("hidden", !showDetails)
    if (clientId) this.fillPanel()
    this.refreshButtons()
    this.refreshRelationLines()
  }

  togglePanelInfo() {
    if (!this.selectedId) return
    this.setSidePanel("details", !this.sidePanelOpen("details"))
    this.refreshButtons()
  }

  // Gestrichelte Linien: Anker des selektierten Teils + sein gehefteter Anhang
  refreshRelationLines() {
    if (!this.selectedId) return this.renderer.setRelations([])
    const links = []
    const part = this.doc.getPart(this.selectedId)
    const anchorId = part && anchorOf(part)
    if (anchorId && this.doc.getPart(anchorId)) links.push([ this.selectedId, anchorId ])
    for (const id of descendantIdsOf(this.doc, this.selectedId)) {
      const follower = this.doc.getPart(id)
      const followerAnchor = anchorOf(follower)
      if (followerAnchor && this.doc.getPart(followerAnchor)) links.push([ id, followerAnchor ])
    }
    this.renderer.setRelations(links)
  }

  fillPanel() {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    this.nameTarget.value = part.name
    this.lengthTarget.value = part.length_mm
    this.widthTarget.value = part.width_mm
    this.thicknessTarget.value = part.thickness_mm
    this.posXTarget.value = part.pos_x
    this.posYTarget.value = part.pos_y
    this.posZTarget.value = part.pos_z
    this.rotXTarget.value = part.rot_x
    this.rotYTarget.value = part.rot_y
    this.rotZTarget.value = part.rot_z
    this.materialTarget.value = part.material_id ?? ""
    this.grainTarget.value = grainOf(part) ?? ""
    // Einzelgewicht unter den Maßen — ohne Rohdichte bleibt es ein Strich
    if (this.hasPartWeightTarget) {
      const kg = partWeight(part, this.doc.getMaterial(part.material_id))
      this.partWeightTarget.textContent = kg === null ? "Gewicht —" : `Gewicht ${formatWeight(kg)}`
      this.partWeightTarget.title = kg === null ? "Keine Rohdichte am Material gepflegt" : ""
    }
    this.renderDeflection(part)
    // Zeigt den Typ, mit dem die Montageanleitung rechnet — ohne gesetztes
    // meta.part_type ist das der aus Name/Bohrbild geratene Wert.
    if (this.hasPartTypeTarget) this.partTypeTarget.value = effectivePartType(part)
    this.renderCutouts(part)
    this.renderEdgeButtons(part)
    this.renderAttachInfo(part)
    this.renderConstraints(part)
    this.renderCorners(part)
    this.renderGrooves(part)
    this.renderMiterButtons(part)
    this.cutoutBadgeTarget.textContent = cutoutsOf(part).length > 0 ? `(${cutoutsOf(part).length})` : ""
    const machinings = groovesOf(part).length + mitersOf(part).length
    this.grooveBadgeTarget.textContent = machinings > 0 ? `(${machinings})` : ""
    const hardwareCount = hardwareOf(part).reduce((sum, entry) => sum + entry.count, 0)
    this.hardwareBadgeTarget.textContent = hardwareCount > 0 ? `(${hardwareCount})` : ""
    this.renderHardware(part)
    this.renderSubBar()

    // Formteile (freie Kontur): L/B sind die Bounding-Box (nicht editierbar),
    // Rechteck-Semantiken (Ecken, Umleimer-Kanten) gelten dort nicht
    const isOutline = Boolean(part.meta?.outline)
    // Gebundene Maße kommen aus der Variablen — Direkteingabe würde die
    // Bindung stillschweigend aushebeln, das Feld ist deshalb gesperrt
    this.fillBindSelects(part)
    this.lengthTarget.disabled = isOutline || boundName(part, "length_mm") !== null
    this.widthTarget.disabled = isOutline || boundName(part, "width_mm") !== null
    this.thicknessTarget.disabled = boundName(part, "thickness_mm") !== null
    this.sectionTargets.forEach((el) => {
      if ([ "ecken", "kanten" ].includes(el.dataset.section)) el.classList.toggle("hidden", isOutline)
    })
  }

  // Durchbiegung des selektierten Teils, direkt unter den Maßen — dort wird
  // sie gebraucht, weil Stärke und Spannweite genau hier verändert werden.
  // Angezeigt nur, wenn checks.js das Teil als waagerechten Boden mit zwei
  // Auflagern erkennt (dieselbe Erkennung wie in der Prüfliste); sonst gäbe
  // die Zahl eine Tragsituation vor, die es gar nicht gibt.
  renderDeflection(part) {
    if (!this.hasDeflectionTarget) return
    const el = this.deflectionTarget
    const material = this.doc.getMaterial(part.material_id)
    const result = shelfAssessment(part, this.doc.allParts(), material?.name ?? null)
    el.classList.toggle("hidden", !result)
    if (!result) return

    const mm = (value) => value.toFixed(1).replace(".", ",")
    el.textContent = `Durchbiegung ≈ ${mm(result.deflectionMm)} mm (Grenze ${mm(result.limitMm)} mm)`
    // Rot über der Grenze, Amber ab drei Vierteln davon — so sieht man beim
    // Ziehen an Stärke oder Spannweite kommen, dass es knapp wird.
    const critical = result.critical
    const tight = !critical && result.deflectionMm > result.limitMm * 0.75
    el.classList.toggle("text-red-400", critical)
    el.classList.toggle("text-amber-400", tight)
    el.classList.toggle("text-stone-400", !critical && !tight)
    el.title = `Freie Spannweite ${Math.round(result.spanMm)} mm, ` +
      `Last ${SHELF_LOAD_KG_PER_SQM} kg/m², Grenze L/300` +
      (material?.name ? ` · ${material.name}` : " · ohne Material konservativ als Spanplatte gerechnet")
  }

  // --- Nut & Falz -----------------------------------------------------------

  renderGrooves(part) {
    const list = this.grooveListTarget
    list.replaceChildren()
    groovesOf(part).forEach((groove, index) => list.appendChild(this.buildGrooveRow(groove, index)))
  }

  buildGrooveRow(groove, index) {
    const row = document.createElement("div")
    row.className = "rounded-lg border border-stone-600 p-2"

    const head = document.createElement("div")
    head.className = "flex items-center justify-between"
    const title = document.createElement("span")
    title.className = "text-xs text-stone-400"
    title.textContent = isFalz(groove) ? `Falz ${index + 1}` : `Nut ${index + 1}`
    const side = document.createElement("button")
    side.className = "text-xs text-sky-300 hover:text-sky-200 min-h-8 px-2"
    side.textContent = groove.side === "bottom" ? "von unten" : "von oben"
    side.addEventListener("click", () =>
      this.applyGroove(index, "side", groove.side === "bottom" ? "top" : "bottom"))
    const remove = document.createElement("button")
    remove.className = "text-xs text-stone-400 hover:text-red-400 min-h-8 px-2"
    remove.textContent = "Entfernen"
    remove.addEventListener("click", () => this.removeGroove(index))
    head.append(title, side, remove)

    const grid = document.createElement("div")
    grid.className = isFalz(groove) ? "grid gap-1 grid-cols-3" : "grid gap-1 grid-cols-4"

    const select = document.createElement("select")
    select.className = "rounded-lg bg-stone-900 border border-stone-600 px-1 py-1.5 text-stone-100 text-xs min-w-0"
    const options = isFalz(groove)
      ? [["l1", "Vorn"], ["l2", "Hinten"], ["b1", "Links"], ["b2", "Rechts"]]
      : [["x", "längs"], ["z", "quer"]]
    for (const [value, label] of options) select.add(new Option(label, value))
    select.value = isFalz(groove) ? groove.edge : groove.direction
    select.addEventListener("change", (event) =>
      this.applyGroove(index, isFalz(groove) ? "edge" : "direction", event.target.value))
    const selectWrap = document.createElement("label")
    selectWrap.className = "text-center text-[10px] text-stone-400 flex flex-col"
    selectWrap.textContent = isFalz(groove) ? "Kante" : "Richtung"
    selectWrap.appendChild(select)
    grid.appendChild(selectWrap)

    const fields = isFalz(groove)
      ? [["width_mm", "B"], ["depth_mm", "T"]]
      : [["width_mm", "B"], ["depth_mm", "T"], ["offset_mm", "A"]]
    for (const [field, label] of fields) {
      const wrap = document.createElement("label")
      wrap.className = "text-center text-[10px] text-stone-400"
      wrap.textContent = label
      if (field === "offset_mm") wrap.title = "Abstand Kante → Nutmitte"
      const input = document.createElement("input")
      input.type = "number"
      input.inputMode = "numeric"
      input.value = groove[field]
      input.className = "mt-0.5 w-full rounded bg-stone-900 border border-stone-600 px-1 py-1.5 text-stone-100 text-xs text-center"
      input.addEventListener("change", (event) => this.applyGroove(index, field, event.target.value))
      wrap.appendChild(input)
      grid.appendChild(wrap)
    }

    row.append(head, grid)
    return row
  }

  addNut() {
    this.appendGroove({ type: "nut", direction: "x", width_mm: 8, depth_mm: 8, offset_mm: 20 })
  }

  addFalz() {
    this.appendGroove({ type: "falz", edge: "l2", width_mm: 12, depth_mm: 9 })
  }

  appendGroove(template) {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    const groove = clampGroove(template, part)
    const grooves = [...groovesOf(part), groove]
    let meta = withGrooves(part, grooves)
    // Exklusivität: ein neuer Falz verdrängt die Gehrung auf seiner Kante
    if (isFalz(groove)) meta = withoutMiter(meta, groove.edge)
    this.history.execute(new UpdatePartCommand(this.selectedId, { meta }))
  }

  applyGroove(index, field, raw) {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    let value = raw
    if (![ "side", "edge", "direction" ].includes(field)) {
      value = parseFloat(raw)
      if (!Number.isFinite(value)) return this.fillPanel()
    }
    const grooves = groovesOf(part).map((groove, i) =>
      i === index ? clampGroove({ ...groove, [field]: value }, part) : groove
    )
    let meta = withGrooves(part, grooves)
    // Falz auf eine Kante mit Gehrung umgehängt → Gehrung dort entfernen
    if (field === "edge") meta = withoutMiter(meta, grooves[index].edge)
    this.history.execute(new UpdatePartCommand(this.selectedId, { meta }))
  }

  // --- Gehrung 45° ----------------------------------------------------------

  renderMiterButtons(part) {
    const mitered = mitersOf(part).map((miter) => miter.edge)
    this.miterButtonTargets.forEach((button) => {
      const on = mitered.includes(button.dataset.editorEdgeParam)
      button.classList.toggle("bg-amber-600", on)
      button.classList.toggle("border-amber-500", on)
      button.classList.toggle("text-white", on)
      button.classList.toggle("text-stone-300", !on)
    })
  }

  toggleMiter(event) {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    this.history.execute(new UpdatePartCommand(this.selectedId, { meta: withMiter(part, event.params.edge) }))
  }

  removeGroove(index) {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    const grooves = groovesOf(part).filter((_, i) => i !== index)
    this.history.execute(new UpdatePartCommand(this.selectedId, { meta: withGrooves(part, grooves) }))
  }

  renderCorners(part) {
    const corners = cornersOf(part)
    for (const key of CORNER_KEYS) {
      const typeSelect = this.cornerTypeTargets.find((el) => el.dataset.corner === key)
      const sizeInput = this.cornerSizeTargets.find((el) => el.dataset.corner === key)
      const treatment = corners[key]
      typeSelect.value = treatment?.type ?? ""
      sizeInput.value = treatment?.size_mm ?? ""
      sizeInput.disabled = !treatment
    }
  }

  applyCorner(event) {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    const key = event.target.dataset.corner
    const type = this.cornerTypeTargets.find((el) => el.dataset.corner === key).value
    const sizeRaw = this.cornerSizeTargets.find((el) => el.dataset.corner === key).value
    const treatment = type === "" ? null : { type, size_mm: parseFloat(sizeRaw) }
    this.history.execute(new UpdatePartCommand(this.selectedId, { meta: withCorner(part, key, treatment) }))
  }

  renderAttachInfo(part) {
    const anchorId = anchorOf(part)
    this.attachInfoTarget.classList.toggle("hidden", !anchorId)
    if (anchorId) {
      this.anchorNameTarget.textContent = this.doc.getPart(anchorId)?.name ?? "(gelöschtes Teil)"
    }
  }

  renderEdgeButtons(part) {
    const active = edgesOf(part)
    this.edgeButtonTargets.forEach((button) => {
      const on = active.includes(button.dataset.editorEdgeParam)
      button.classList.toggle("bg-amber-600", on)
      button.classList.toggle("border-amber-500", on)
      button.classList.toggle("text-white", on)
      button.classList.toggle("text-stone-300", !on)
    })
  }

  toggleEdgeBand(event) {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    this.history.execute(new UpdatePartCommand(this.selectedId, { meta: toggleEdge(part, event.params.edge) }))
  }

  // --- Ausschnitte ----------------------------------------------------------

  // Bohrungen einer Lochreihe bekommen eine gemeinsame Kopfzeile (Anzahl +
  // „Reihe entfernen“) vor der ersten Bohrung der Reihe; die Einzel-Einträge
  // bleiben daneben bestehen und damit einzeln editier- und löschbar.
  renderCutouts(part) {
    const list = this.cutoutListTarget
    list.replaceChildren()
    const seenRows = new Set()
    cutoutsOf(part).forEach((cutout, index) => {
      const rowId = holeRowIdOf(cutout)
      if (rowId && !seenRows.has(rowId)) {
        seenRows.add(rowId)
        list.appendChild(this.buildHoleRowHeader(part, rowId))
      }
      list.appendChild(this.buildCutoutRow(cutout, index))
    })
  }

  buildHoleRowHeader(part, rowId) {
    const head = document.createElement("div")
    head.className = "flex items-center justify-between rounded-lg bg-stone-700/40 px-2 py-1"
    head.dataset.holeRow = rowId
    const title = document.createElement("span")
    title.className = "text-xs text-amber-400"
    title.textContent = `Lochreihe (${holeRowSize(part, rowId)})`
    const remove = document.createElement("button")
    remove.className = "text-xs text-stone-400 hover:text-red-400 min-h-8 px-2"
    remove.textContent = "Reihe entfernen"
    remove.title = "Alle Bohrungen dieser Reihe in einem Schritt entfernen"
    remove.addEventListener("click", () => this.removeHoleRow(rowId))
    head.append(title, remove)
    return head
  }

  // Ganze Reihe in einem Undo-Schritt entfernen
  removeHoleRow(rowId) {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    const cutouts = withoutHoleRow(part, rowId)
    if (cutouts.length === cutoutsOf(part).length) return
    this.history.execute(new UpdatePartCommand(this.selectedId, { meta: withCutouts(part, cutouts) }))
  }

  buildCutoutRow(cutout, index) {
    const part = this.doc.getPart(this.selectedId)
    const row = document.createElement("div")
    row.className = "rounded-lg border border-stone-600 p-2"

    const head = document.createElement("div")
    head.className = "flex items-center justify-between"
    const title = document.createElement("span")
    title.className = "text-xs text-stone-400"
    title.textContent = isCircle(cutout) ? `Bohrung ${index + 1}` : `Ausschnitt ${index + 1}`
    const remove = document.createElement("button")
    remove.className = "text-xs text-stone-400 hover:text-red-400 min-h-8 px-2"
    remove.textContent = "Entfernen"
    remove.addEventListener("click", () => this.removeCutout(index))
    head.append(title, remove)

    // Gruppen-Kennzeichen: Bohrung gehört zu einer Lochreihe
    if (holeRowIdOf(cutout)) {
      title.className += " inline-flex items-center gap-1"
      const badge = document.createElement("span")
      badge.className = "rounded bg-stone-700 px-1 py-0.5 text-[10px] text-amber-400"
      badge.textContent = "Reihe"
      badge.title = "Teil einer Lochreihe"
      title.appendChild(badge)
    }

    const axis = cutoutAxis(cutout)

    if (isCircle(cutout)) {
      // Bohrachse: Fläche (Stärke) oder Stirnseite längs/quer
      const axisSelect = document.createElement("select")
      axisSelect.className = "text-xs min-h-8 bg-stone-900 border border-stone-600 rounded px-1 text-stone-200"
      axisSelect.title = "Bohrachse"
      for (const [ value, label ] of [ [ "y", "Fläche" ], [ "x", "Stirn längs" ], [ "z", "Stirn quer" ] ]) {
        axisSelect.add(new Option(label, value, false, value === axis))
      }
      axisSelect.addEventListener("change", (event) => this.applyCutout(index, "axis", event.target.value))
      head.insertBefore(axisSelect, remove)
    }

    if (isCircle(cutout) && cutout.depth_mm) {
      const side = document.createElement("button")
      side.className = "text-xs text-sky-300 hover:text-sky-200 min-h-8 px-2"
      side.textContent = CUTOUT_SIDE_LABELS[axis][cutout.side === "bottom" ? "bottom" : "top"]
      side.title = "Bohrseite wechseln"
      side.addEventListener("click", () =>
        this.applyCutout(index, "side", cutout.side === "bottom" ? "top" : "bottom"))
      head.insertBefore(side, remove)
    }

    // Stirnbohrung: Lage quer zur Bohrachse (X bzw. Z ab Kante) und Y über die Stärke
    const fields = isCircle(cutout)
      ? (axis === "y"
        ? [["diameter_mm", "Ø"], ["x_mm", "X"], ["z_mm", "Z"], ["depth_mm", "T"]]
        : [["diameter_mm", "Ø"], [axis === "x" ? "z_mm" : "x_mm", axis === "x" ? "Z" : "X"], ["y_mm", "Y"], ["depth_mm", "T"]])
      : [["length_mm", "L"], ["width_mm", "B"], ["x_mm", "X"], ["z_mm", "Z"]]

    const grid = document.createElement("div")
    grid.className = "grid gap-1 grid-cols-4"
    for (const [field, label] of fields) {
      const wrap = document.createElement("label")
      wrap.className = "text-center text-[10px] text-stone-400"
      wrap.textContent = label
      const input = document.createElement("input")
      input.type = "number"
      input.inputMode = "decimal"
      input.step = "0.5"
      // X/Z werkstattgerecht ab linker/vorderer Kante anzeigen; T leer = durchgehend
      input.value = field === "x_mm" || field === "z_mm"
        ? toEdgeDistance(cutout, part, field === "x_mm" ? "x" : "z")
        : cutout[field] ?? ""
      if (field === "depth_mm") input.placeholder = axis === "y" ? "durch" : "Tiefe"
      if (field === "y_mm") input.placeholder = "Mitte"
      input.className = "mt-0.5 w-full rounded bg-stone-900 border border-stone-600 px-1 py-1.5 text-stone-100 text-xs text-center"
      input.addEventListener("change", (event) => this.applyCutout(index, field, event.target.value))
      wrap.appendChild(input)
      grid.appendChild(wrap)
    }

    row.append(head, grid)
    return row
  }

  addCutout() {
    this.appendCutout({ length_mm: 100, width_mm: 100, x_mm: 0, z_mm: 0 })
  }

  addDrill() {
    this.appendCutout({ shape: "circle", diameter_mm: 35, x_mm: 0, z_mm: 0 })
  }

  appendCutout(template) {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    const cutouts = [...cutoutsOf(part), clampCutout(template, part)]
    this.history.execute(new UpdatePartCommand(this.selectedId, { meta: withCutouts(part, cutouts) }))
  }

  applyCutout(index, field, raw) {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    let value
    if (field === "side" || field === "axis") {
      value = raw
    } else if (field === "depth_mm" && String(raw).trim() === "") {
      value = 0 // leer = durchgehend (clampCutout verwirft die Tiefe)
    } else {
      value = parseFloat(raw)
      if (!Number.isFinite(value)) return this.fillPanel()
    }
    const cutouts = cutoutsOf(part).map((cutout, i) => {
      if (i !== index) return cutout
      if (field === "x_mm" || field === "z_mm") {
        value = fromEdgeDistance(value, cutout, part, field === "x_mm" ? "x" : "z")
      }
      return clampCutout({ ...cutout, [field]: value }, part)
    })
    this.history.execute(new UpdatePartCommand(this.selectedId, { meta: withCutouts(part, cutouts) }))
  }

  removeCutout(index) {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    const cutouts = cutoutsOf(part).filter((_, i) => i !== index)
    this.history.execute(new UpdatePartCommand(this.selectedId, { meta: withCutouts(part, cutouts) }))
  }

  populateMaterialSelect() {
    const select = this.materialTarget
    select.replaceChildren()
    select.add(new Option("— kein Material —", ""))
    for (const material of this.doc.materials.values()) {
      select.add(new Option(material.name, material.id))
    }
  }

  applyMaterial(event) {
    if (!this.selectedId) return
    const materialId = event.target.value === "" ? null : parseInt(event.target.value, 10)
    const material = this.doc.getMaterial(materialId)
    const attrs = { material_id: materialId }
    if (material?.default_thickness_mm) attrs.thickness_mm = material.default_thickness_mm
    this.history.execute(new UpdatePartCommand(this.selectedId, attrs))
  }

  // Maserrichtung — steckt im meta und wandert damit beim Duplizieren/Mustern mit
  applyGrain(event) {
    if (!this.selectedId) return
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    this.history.execute(new UpdatePartCommand(this.selectedId, { meta: withGrain(part, event.target.value) }))
  }

  // Bauteil-Typ von Hand setzen — für umbenannte oder selbst gezeichnete
  // Teile, die die Montage sonst falsch einsortiert (editor/part_types.js).
  applyPartType(event) {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    this.history.execute(new UpdatePartCommand(this.selectedId, { meta: withPartType(part, event.target.value) }))
  }

  applyPanel(event) {
    if (!this.selectedId) return
    const field = event.target.dataset.field
    const raw = event.target.value
    const value = field === "name" ? raw : parseInt(raw, 10)

    if (field !== "name" && !Number.isFinite(value)) return this.fillPanel()
    if (["length_mm", "width_mm", "thickness_mm"].includes(field) && value <= 0) return this.fillPanel()
    if (field === "name" && raw.trim() === "") return this.fillPanel()

    // Positionsänderungen nehmen geheftete Teile mit
    if (["pos_x", "pos_y", "pos_z"].includes(field)) {
      return this.movePart(this.selectedId, { [field]: value })
    }
    // Drehungen laufen über denselben Weg wie 90°-Buttons und Gizmo-Ring
    if (["rot_x", "rot_y", "rot_z"].includes(field)) {
      return this.rotatePart(this.selectedId, { [field]: value })
    }

    const attrs = { [field]: value }
    // Schrumpfende Platte: Bearbeitungen im selben Command nach innen klemmen
    const part = this.doc.getPart(this.selectedId)
    if (["length_mm", "width_mm", "thickness_mm"].includes(field)) {
      const meta = this.resizedMeta(part, { ...part, [field]: value })
      if (meta) attrs.meta = meta
    }

    this.executeWithConstraints(this.selectedId, attrs)
  }

  // --- Toolbar-Aktionen -----------------------------------------------------

  addPlate() { this.addPreset(PRESETS.plate) }
  addBeam() { this.addPreset(PRESETS.beam) }

  addPreset(preset) {
    const offset = (this.doc.allParts().length % 8) * 150
    const part = newPart({
      ...preset,
      pos_x: offset,
      pos_y: Math.round(preset.thickness_mm / 2),
      pos_z: offset
    })
    this.history.execute(new AddPartCommand(part))
    this.select(part.client_id)
  }

  // Spiegeln links ↔ rechts: Bearbeitungen wandern auf die andere Seite
  mirror() {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    this.history.execute(new UpdatePartCommand(this.selectedId, { meta: mirroredMeta(part) }))
  }

  // --- Beschläge & Verbinder -------------------------------------------------

  addHardware(event) {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    this.history.execute(new UpdatePartCommand(this.selectedId, { meta: withHardware(part, event.params.preset) }))
  }

  removeHardware(index) {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    this.history.execute(new UpdatePartCommand(this.selectedId, { meta: withoutHardware(part, index) }))
  }

  renderHardware(part) {
    const list = this.hardwareListTarget
    list.replaceChildren()
    hardwareOf(part).forEach((entry, index) => {
      const row = document.createElement("div")
      row.className = "flex items-center justify-between rounded-lg border border-stone-600 px-2 py-1"
      const label = document.createElement("span")
      label.className = "text-xs text-stone-200"
      label.textContent = `${entry.count}× ${entry.item}`
      const remove = document.createElement("button")
      remove.className = "text-xs text-stone-400 hover:text-red-400 min-h-8 px-2"
      remove.textContent = "Entfernen"
      remove.addEventListener("click", () => this.removeHardware(index))
      row.append(label, remove)
      list.appendChild(row)
    })
  }

  duplicate() {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    // Gruppenmitglied: die ganze Baugruppe als Satz kopieren — derselbe
    // Remap-Codepfad wie multiDuplicate (frische Gruppen-Id, Anker intern)
    const group = groupOf(part)
    if (group) {
      const members = this.groupMembers(group.id)
      const copies = duplicatedParts(members, { x: 100, y: 0, z: 100 })
      this.history.execute(new CompositeCommand(copies.map((copy) => new AddPartCommand(copy))))
      this.select(copies[members.findIndex((member) => member.client_id === part.client_id)].client_id)
      return
    }
    const copy = newPart({ ...structuredClone(part), client_id: undefined, pos_x: part.pos_x + 100, pos_z: part.pos_z + 100 })
    this.history.execute(new AddPartCommand(copy))
    this.select(copy.client_id)
  }

  removeSelected() {
    if (!this.selectedId) return
    this.history.execute(new RemovePartCommand(this.selectedId))
  }

  rotate(event) {
    const part = this.doc.getPart(this.selectedId)
    if (!part) return
    const axis = event.params.axis
    this.rotatePart(this.selectedId, { [axis]: snapAngle(part[axis] + 90) })
  }

  undo() { this.history.undo(); this.refreshButtons() }
  redo() { this.history.redo(); this.refreshButtons() }

  setGrid(event) {
    this.gridMm = parseInt(event.target.value, 10) || 10
  }

  reload() {
    location.reload()
  }

  // Pfeiltasten: Selektion im aktuellen Raster entlang der Weltachsen
  // verschieben (Shift = ×10); nutzt movePart — angeheftete Teile und
  // Abstands-Constraints wandern mit, ein Undo-Schritt. true = verarbeitet.
  nudgeSelection(key, large) {
    if (this.sketchMode || this.measuring || this.photoMode || this.assemblyMode) return false
    const nudge = nudgeDelta(key, this.gridMm * (large ? 10 : 1))
    if (!nudge) return false
    if (this.multiSelect) {
      const parts = this.multiParts()
      if (parts.length === 0) return false
      const delta = { x: 0, y: 0, z: 0 }
      delta[nudge.field === "pos_x" ? "x" : "z"] = nudge.mm
      this.history.execute(new UpdatePartsCommand(batchMoveUpdates(parts, delta)))
      return true
    }
    const part = this.doc.getPart(this.selectedId)
    if (!part) return false
    this.movePart(this.selectedId, { [nudge.field]: part[nudge.field] + nudge.mm })
    return true
  }

  // --- Befehlspalette & Tastenkürzel ----------------------------------------
  // Die Werkzeugliste steht ausschließlich in editor/commands_registry.js;
  // Palette, Kürzel und Tooltips lesen alle aus derselben Quelle.

  togglePalette() {
    if (this.paletteOpen) this.closePalette()
    else this.openPalette()
  }

  openPalette() {
    if (!this.hasPaletteDialogTarget) return
    this.paletteOpen = true
    this.paletteIndex = 0
    this.paletteInputTarget.value = ""
    this.paletteDialogTarget.classList.remove("hidden")
    this.renderPalette()
    this.paletteInputTarget.focus()
  }

  closePalette() {
    if (!this.hasPaletteDialogTarget) return
    this.paletteOpen = false
    this.paletteDialogTarget.classList.add("hidden")
    this.paletteInputTarget.blur() // sonst schlucken die Kürzel im Eingabefeld
  }

  // Klick auf den abgedunkelten Rand schließt (wie ein Tipp daneben am Tablet)
  paletteBackdrop(event) {
    if (event.target === this.paletteDialogTarget) this.closePalette()
  }

  filterPalette() {
    this.paletteIndex = 0
    this.renderPalette()
  }

  // Tastatur im Eingabefeld: ↑/↓ wandern, Enter löst aus, Escape schließt
  paletteKeys(event) {
    if (event.key === "ArrowDown") { event.preventDefault(); this.movePaletteSelection(1) }
    else if (event.key === "ArrowUp") { event.preventDefault(); this.movePaletteSelection(-1) }
    else if (event.key === "Enter") { event.preventDefault(); this.runPaletteSelection() }
    else if (event.key === "Escape") { event.preventDefault(); this.closePalette() }
    else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
      event.preventDefault()
      this.closePalette()
    }
  }

  pickPaletteCommand(event) {
    this.paletteIndex = parseInt(event.currentTarget.dataset.paletteIndex, 10)
    this.runPaletteSelection()
  }

  movePaletteSelection(delta) {
    const count = this.paletteResults?.length ?? 0
    if (count === 0) return
    // Gesperrte Zeilen überspringen — sie lassen sich ohnehin nicht auslösen.
    // Ist alles gesperrt, endet die Runde wieder auf der Ausgangszeile.
    for (let step = 0; step < count; step++) {
      this.paletteIndex = (this.paletteIndex + delta + count) % count
      if (this.commandAllowed(this.paletteResults[this.paletteIndex])) break
    }
    this.highlightPaletteRow()
  }

  // Aktive Zeile hervorheben und in den sichtbaren Bereich holen
  highlightPaletteRow() {
    this.paletteListTarget.querySelectorAll("[data-palette-index]").forEach((row, index) => {
      const active = index === this.paletteIndex
      row.classList.toggle("bg-stone-700", active)
      row.classList.toggle("ring-1", active)
      row.classList.toggle("ring-amber-500", active)
      row.setAttribute("aria-selected", active ? "true" : "false")
      if (active) row.scrollIntoView({ block: "nearest" })
    })
  }

  runPaletteSelection() {
    const command = this.paletteResults?.[this.paletteIndex]
    if (!command) return
    this.closePalette()
    this.runCommand(command)
  }

  // Der aktive exklusive Modus — die eine Wahrheit für Kürzel und Palette.
  // Reihenfolge = Verdrängung: Skizze schließt Foto und Montage ohnehin aus.
  get activeMode() {
    if (this.sketchMode) return "sketch"
    if (this.photoMode) return "photo"
    if (this.assemblyMode) return "assembly"
    return "normal"
  }

  commandAllowed(command) {
    return isAllowedInMode(command, this.activeMode)
  }

  // Ein Registereintrag ruft genau seine Controller-Methode auf; Methoden mit
  // Stimulus-Parametern (setView) bekommen ein passendes Ereignis-Objekt.
  // Gesperrte Befehle laufen nicht halb durch, sondern sagen sichtbar Bescheid —
  // stilles Nichtstun war die eigentliche Verwirrung.
  runCommand(command) {
    if (!this.commandAllowed(command)) {
      this.notify(
        `„${command.label}“ ist im ${modeLabel(this.activeMode)} nicht verfügbar`)
      return false
    }
    const method = this[command.method]
    if (typeof method !== "function") return false
    method.call(this, command.params ? { params: command.params } : undefined)
    return true
  }

  renderPalette() {
    const query = this.paletteInputTarget.value
    this.paletteResults = searchCommands(COMMANDS, query)
    // Die Vorauswahl darf nie auf einer gesperrten Zeile stehen — sonst liefe
    // Enter ins Leere (bzw. in die Absage)
    if (!this.commandAllowed(this.paletteResults[this.paletteIndex] ?? {})) {
      const next = this.paletteResults.findIndex((command) => this.commandAllowed(command))
      this.paletteIndex = next >= 0 ? next : 0
    }
    const list = this.paletteListTarget
    list.textContent = ""
    // Im Modus bleibt die Palette vollständig: Verstecken sähe aus, als wären
    // Werkzeuge verschwunden. Gesperrtes steht da, aber ausgegraut und tot —
    // diese Zeile sagt, warum.
    if (this.activeMode !== "normal") {
      const note = document.createElement("p")
      note.className = "px-3 pt-2 pb-1 text-[11px] text-amber-400"
      note.textContent = `${modeLabel(this.activeMode)}: ausgegraute Befehle sind hier gesperrt`
      list.append(note)
    }
    if (this.paletteResults.length === 0) {
      const empty = document.createElement("p")
      empty.className = "px-3 py-6 text-center text-sm text-stone-400"
      empty.textContent = "Kein Befehl gefunden"
      list.append(empty)
      return
    }
    // Leere Eingabe = Kürzel-Übersicht: alle Befehle mit Gruppen-Überschriften
    const grouped = query.trim() === ""
    let lastGroup = null
    this.paletteResults.forEach((command, index) => {
      if (grouped && command.group !== lastGroup) {
        lastGroup = command.group
        const heading = document.createElement("p")
        heading.className = "px-3 pt-3 pb-1 text-[11px] uppercase tracking-wide text-stone-500"
        heading.textContent = command.group
        list.append(heading)
      }
      list.append(this.buildPaletteRow(command, index))
    })
    this.highlightPaletteRow()
  }

  buildPaletteRow(command, index) {
    const row = document.createElement("button")
    row.type = "button"
    row.dataset.paletteIndex = index
    row.setAttribute("role", "option")
    row.className = "w-full flex items-center gap-3 rounded-xl px-3 min-h-11 text-left text-sm text-stone-100 hover:bg-stone-700"
    if (command.hint) row.title = command.hint

    // Gesperrt im aktiven Modus: sichtbar, aber nicht auslösbar
    if (!this.commandAllowed(command)) {
      row.disabled = true
      row.setAttribute("aria-disabled", "true")
      row.classList.add("opacity-40")
      row.classList.remove("hover:bg-stone-700")
      row.title = `Im ${modeLabel(this.activeMode)} nicht verfügbar`
    } else {
      row.dataset.action = "editor#pickPaletteCommand"
    }

    const icon = this.commandIcon(command)
    icon.classList.add("shrink-0", "text-stone-300")
    row.append(icon)

    const label = document.createElement("span")
    label.className = "flex-1 truncate"
    label.textContent = command.label
    row.append(label)

    if (command.shortcut) {
      const keys = document.createElement("kbd")
      keys.className = "shrink-0 rounded-md border border-stone-600 bg-stone-900 px-1.5 py-0.5 text-[11px] text-stone-400 font-sans"
      keys.textContent = shortcutLabel(command.shortcut, this.macKeys)
      row.append(keys)
    }
    return row
  }

  // Icon aus dem vorhandenen Werkzeug-Button klauen — so bleibt die Palette
  // ohne zweite Icon-Liste automatisch beim Bild der Werkzeugleiste.
  commandIcon(command) {
    const svg = this.commandButton(command)?.querySelector("svg")?.cloneNode(true)
    if (svg) {
      svg.setAttribute("class", "w-5 h-5")
      return svg
    }
    const placeholder = document.createElement("span")
    placeholder.className = "w-5 h-5"
    return placeholder
  }

  // Der Button im DOM, der denselben Befehl auslöst (für Icon und Tooltip)
  commandButton(command) {
    let selector = `[data-action~="editor#${command.method}"]`
    for (const [ key, value ] of Object.entries(command.params ?? {})) {
      selector += `[data-editor-${key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}-param="${value}"]`
    }
    return this.element.querySelector(selector)
  }

  // Kürzel stehen nur im Register — die Tooltips der Werkzeug-Buttons werden
  // beim Verbinden daraus ergänzt („Platte hinzufügen (P)"). Bewusst in JS
  // statt im ERB: sonst müsste jede Schaltfläche ihr Kürzel selbst kennen und
  // beide Listen könnten auseinanderlaufen.
  applyShortcutTooltips() {
    for (const command of COMMANDS) {
      if (!command.shortcut) continue
      const button = this.commandButton(command)
      if (!button || button.dataset.shortcutHint) continue
      const keys = shortcutLabel(command.shortcut, this.macKeys)
      button.title = `${button.title || command.label} (${keys})`
      button.dataset.shortcutHint = keys
    }
  }

  // Ein Kürzel aus dem Register ausführen. Schweigt, solange die Palette oder
  // ein Dialog offen ist; Eingabefelder hat keydown() schon abgefangen.
  // Im Skizzen-, Foto- und Montage-Modus laufen nur die dort erlaubten Befehle;
  // die anderen werden abgelehnt (runCommand meldet das in der Statuszeile).
  // Auch der abgelehnte Tastendruck gilt als verbraucht — sonst fiele er in
  // keydown() auf die alten Einzelfälle (Entf, h …) durch und wirkte doch.
  runShortcut(event) {
    if (this.paletteOpen || this.dialogOpen()) return false
    const command = commandForShortcut(COMMANDS, event)
    if (!command) return false
    event.preventDefault()
    this.runCommand(command)
    return true
  }

  dialogOpen() {
    return Array.from(this.element.querySelectorAll("[data-editor-target$='Dialog']"))
      .some((dialog) => !dialog.classList.contains("hidden"))
  }

  keydown(event) {
    if (event.target.matches("input, textarea, select")) return
    if (this.runShortcut(event)) return
    const mod = event.metaKey || event.ctrlKey
    if (mod && event.key === "z" && !event.shiftKey) { event.preventDefault(); this.undo() }
    else if (mod && (event.key === "Z" || event.key === "y")) { event.preventDefault(); this.redo() }
    // Strg+D und die Pfeiltasten stehen nicht im Register, greifen aber ins
    // Dokument — im Modus wären sie die Hintertür um die Sperre herum
    else if (this.activeMode !== "normal" && ((mod && event.key === "d") || event.key.startsWith("Arrow"))) {
      if (mod) event.preventDefault()
      this.notify(`Im ${modeLabel(this.activeMode)} nicht verfügbar`)
    }
    else if (mod && event.key === "d") {
      // Strg/Cmd+D: exakt die Duplizieren-Aktion der Kontext- bzw. Multi-Leiste
      event.preventDefault()
      if (this.multiSelect) this.multiDuplicate()
      else this.duplicate()
    }
    else if (event.key === "Delete" || event.key === "Backspace") { event.preventDefault(); this.removeSelected() }
    else if (!mod && event.key === "h") { this.toggleHiddenSelection() }
    else if (!mod && event.key.startsWith("Arrow")) {
      if (this.nudgeSelection(event.key, event.shiftKey)) event.preventDefault()
    }
    else if (event.key === "Escape") {
      if (this.paletteOpen) this.closePalette()
      else if (this.closeOpenDialog()) { /* Dialog hatte Vorrang */ }
      else if (this.multiSelect) this.finishMultiSelect()
      else if (this.sketchMode?.pickingPlane) {
        this.sketchMode.pickingPlane = false
        this.updateSketchUi()
      }
      else if (this.sketchMode?.selectedEdge || this.sketchMode?.selectedCorner || this.sketchMode?.selectedFaces?.length) {
        this.clearSketchSelection()
        this.renderSketch()
        this.updateSketchUi()
      }
      else if (this.sketchMode) this.cancelSketch()
      else if (this.photoMode) this.exitPhoto()
      else if (this.assemblyMode) this.finishAssembly()
      else if (this.measuring) this.stopMeasure()
      else if (this.sectionView) this.stopSection()
      else if (this.pickMode) this.cancelPick()
      else if (this.subSelection) this.setSubSelection(null)
      else this.select(null)
    }
  }

  // Escape schließt einen offenen Dialog. Konvention: Target "cabinetDialog"
  // → Methode "closeCabinetDialog" (die räumt auch Bearbeiten-Zustände auf);
  // fehlt sie, wird schlicht ausgeblendet.
  closeOpenDialog() {
    const dialog = this.element.querySelector("[data-editor-target$='Dialog']:not(.hidden)")
    if (!dialog) return false
    const target = dialog.dataset.editorTarget.split(" ").find((name) => name.endsWith("Dialog"))
    const upper = `${target[0].toUpperCase()}${target.slice(1)}`
    // "cabinetDialog" → closeCabinetDialog, ersatzweise ohne Suffix
    // ("paletteDialog" → closePalette); sonst schlicht ausblenden
    const method = [ `close${upper}`, `close${upper.replace(/Dialog$/, "")}` ]
      .find((name) => typeof this[name] === "function")
    if (method) this[method]()
    else dialog.classList.add("hidden")
    return true
  }

  // --- Status ---------------------------------------------------------------

  refreshButtons() {
    if (this.hasUndoButtonTarget) this.undoButtonTarget.disabled = !this.history.canUndo
    if (this.hasRedoButtonTarget) this.redoButtonTarget.disabled = !this.history.canRedo
    this.contextBarTarget.classList.toggle("hidden", !this.selectedId)
    if (this.hasCabinetEditButtonTarget) {
      const inCabinet = Boolean(this.cabinetAnchorFor(this.doc.getPart(this.selectedId)))
      this.cabinetEditButtonTarget.classList.toggle("hidden", !inCabinet)
    }
    // Baugruppen-Label (Klick = umbenennen) + „Gruppe aufheben“ nur bei
    // Gruppenmitglied; während des Umbenennens bleibt das Eingabefeld vorn
    if (this.hasGroupLabelTarget) {
      const selectedPart = this.doc.getPart(this.selectedId)
      const group = selectedPart ? groupOf(selectedPart) : null
      if (!group) this.groupNameInputTarget.classList.add("hidden")
      const renaming = !this.groupNameInputTarget.classList.contains("hidden")
      this.groupLabelTarget.textContent = group?.name ?? ""
      this.groupLabelTarget.classList.toggle("hidden", !group || renaming)
      this.ungroupButtonTarget.classList.toggle("hidden", !group)
    }

    if (this.hasDrawerEditButtonTarget) {
      // Ein Button für beide Gruppen — Beschriftung/Icon je nach Fund
      const group = this.drawerAnchorFor(this.doc.getPart(this.selectedId))
      const door = group?.kind === "door"
      this.drawerEditButtonTarget.classList.toggle("hidden", !group)
      this.drawerEditButtonTarget.setAttribute("aria-label", door ? "Tür…" : "Schublade…")
      this.drawerEditButtonTarget.title = door ? "Tür bearbeiten" : "Schublade bearbeiten"
      if (this.hasDrawerEditIconTarget) this.drawerEditIconTarget.classList.toggle("hidden", door)
      if (this.hasDoorEditIconTarget) this.doorEditIconTarget.classList.toggle("hidden", !door)
    }
    this.refreshSidePanelButtons()
  }

  // Kontextleiste dem selektierten Teil nachführen (Aufruf pro Frame aus dem
  // Renderer). Bleibt im sichtbaren Bereich, oberhalb der Werkzeugleiste.
  positionContextBar(pos) {
    const bar = this.contextBarTarget
    if (!pos || bar.classList.contains("hidden")) return
    const host = bar.parentElement
    const margin = 8
    const x = Math.min(Math.max(pos.x - bar.offsetWidth / 2, margin), host.clientWidth - bar.offsetWidth - margin)
    const y = Math.min(Math.max(pos.y - bar.offsetHeight - 14, margin), host.clientHeight - bar.offsetHeight - 72)
    bar.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`
  }

  // Meldungen ("4 Verbinder gesetzt", "Korpusmaße unplausibel", Modus-Sperre …)
  // teilen sich das Feld mit dem Speicherstatus. Ohne Vorrang überschreibt der
  // nächste Autosave sie nach Sekundenbruchteilen, und der Nutzer liest sie nie.
  notify(text) {
    this.statusTarget.textContent = text
    this.statusTarget.className = "text-xs shrink-0 text-amber-400"
    this.noticeUntil = Date.now() + NOTICE_MS
    clearTimeout(this.noticeTimer)
    this.noticeTimer = setTimeout(() => {
      this.noticeUntil = 0
      if (this.lastStatus) this.renderStatus(...this.lastStatus)
    }, NOTICE_MS)
  }

  renderStatus(state, detail) {
    this.lastStatus = [ state, detail ]
    // Konflikt und Fehler haben Vorrang auch vor einer frischen Meldung
    if (Date.now() < (this.noticeUntil ?? 0) && state !== "conflict" && state !== "error") return
    if (state === "conflict") {
      // Aus der Vorwarnung ist Ernst geworden — der Nachzieh-Hinweis tritt ab,
      // beide säßen sonst übereinander auf demselben Platz
      this.hideCollabHint()
      this.conflictTarget.classList.remove("hidden")
      this.statusTarget.textContent = "Konflikt"
      return
    }
    const time = detail instanceof Date ? detail.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : ""
    this.statusTarget.textContent = {
      dirty: "Ungespeichert",
      saving: "Speichert…",
      saved: `Gespeichert ${time}`,
      error: "Fehler — neuer Versuch…",
      offline: "Offline — Änderungen werden lokal gehalten"
    }[state] || ""
    // Zustand auch farblich lesbar: ruhig wenn gespeichert, warm bei Arbeit
    this.statusTarget.className = "text-xs shrink-0 " + ({
      saved: "text-stone-400",
      error: "text-red-400",
      offline: "text-sky-400"
    }[state] || "text-amber-400")
  }
}
